/**
 * S5.7-F — der Reiter „Betrieb" der Seite „Kundenrechnungen" (`/invoicing?tab=betrieb`).
 * NUR LESEND.
 *
 * Vier Betriebsflächen der Rechnungsstraße, je ein einklappbarer Abschnitt
 * (seltene Bedienung). Sie lesen genau die vier Routen
 * (`backend/invoicing/betrieb_routes.py`) und schreiben nichts:
 *
 *   GET /api/invoicing/admin/nummernkreise              → (a) Kreise, Lücke, Folgejahr
 *   GET /api/invoicing/admin/steuerbeobachtung?von&bis  → (b) Matrix, Abweichungen, Sperren
 *   GET /api/invoicing/admin/tagesabschluss?tag         → (c) Forge / Refund / BB / Läufe
 *   GET /api/invoicing/admin/igl-nachweise              → (d) IgL-Nachweise + Störungslage
 *
 * 🔴 **Keine Aussage über das hinaus, was die API liefert.** Texte von Befunden
 *    (`abweichungen[].text`, `laeufe[].warnung`) stehen wörtlich da; die Seite
 *    urteilt nicht selbst über Steuer. `auffaellig` ist das Urteil des Backends,
 *    die Lücke steht als Zahl daneben — auch negativ (Regel 17 §3b).
 *
 * 🔴 **Zwei Zeitbegriffe im Tagesabschluss** (`invoicing.md` §S5.7a–c). `?tag=`
 *    datiert nur die TAGESWERTE (ausgestellt, korrigiert, versandt, abgewiesen,
 *    neue Refund-Entscheidungen). Offene Sperren, Einträge ohne Nummer, die
 *    Refund-Zustände und der Modus sind der Stand JETZT — so zählt `lagen.kennzahlen`
 *    sie, ohne Tagesgrenze; jede Kachel sagt deshalb in ihrem Hinweis, welcher
 *    Bezug gilt. BB und Läufe tragen `stand: "jetzt"` in der Antwort; bei JEDEM
 *    Tag außer heute (vergangen wie künftig) steht das als „Momentaufnahme jetzt"
 *    sichtbar da. „Heute" wird bei jedem Aufbau frisch bestimmt, nicht beim ersten
 *    eingefroren — nach Mitternacht ist der Vortag ein anderer Tag. Das Tagesfeld
 *    endet bei heute (`max`): ein künftiger Tag hätte nur Nullen.
 *
 * 🔴 **Zustände wie S5.4-F** (`InvoiceList.jsx`): erstes Laden = Skeletons, nie
 *    Spinner; Neuladen lässt die Daten stehen (`LinearProgress`, `aria-busy`);
 *    ein Fehler je Abschnitt mit „Erneut versuchen" — und dann KEINE alte Zahl.
 *    Ein ungültiger Zeitraum (> 366 Tage, Bis vor Von, leer) zeigt einen
 *    Feldhinweis und fragt NICHT an — das Backend lehnte ihn mit 400 ab. Steht
 *    dabei noch ein Fehler, ist „Erneut versuchen" gesperrt und sagt warum: ein
 *    Knopf, der nichts auslöst, wäre eine stille Sackgasse.
 *
 * 🔴 **Kontrast:** jede Pill steht auf einer Fläche mit `elevation={0}` (Tabelle,
 *    Karte) — auf elevation 1 fiele `info` im Dunkelmodus unter 4,5:1
 *    (`InvoiceList.jsx`, `STATUS_TON`). Gepinnt in `__tests__/Betrieb.test.jsx`,
 *    „jede Pill des Reiters erreicht 4,5:1 …".
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import CollapsibleSection from '../../components/common/CollapsibleSection'
import EmptyState from '../../components/common/EmptyState'
import Fachbegriff from '../../components/common/Fachbegriff'
import StatTile from '../../components/common/StatTile'
import KpiRow, { KpiCell } from '../../components/common/KpiRow'
import Pill from '../../components/common/Pill'
import { api } from '../../api/client'
import { CONTENT_WIDTH, FS, MOBILE_APP_BAR_CLEARANCE, PANEL_SX, SP, focusRing } from '../../theme/tokens'
import { scrollIntoViewRespectingMotion } from '../../utils/scroll'
import { markiereSprungziel } from '../../utils/sprungziel'
import { berlinDateISO, formatBerlinDate, formatBerlinDateTime } from '../../utils/time'
import {
  ART_LABEL, BB_LABEL, RECHTSTRAEGER_LABEL, STATUS_LABEL, STEUERFALL_LABEL, bestellzeile, modusText,
} from './beschriftungen'
import BestellLink from './BestellLink'
import DisabledReason from '../../components/common/DisabledReason';

// ── Beschriftungen ─────────────────────────────────────────────────────────
// Geschlossene Mengen sind in `__tests__/Betrieb.test.jsx` gegen ihre Quelle
// gelesen (Migration 326 und 339, `betrieb_routes.py`, `cron_runner.py`,
// `tax_plausibility.py`, `bb_zustand.py`, `outbox.py`, `issuance.py`) — eine neue
// Quelle macht den Test rot, nicht die Seite still.

const STRICH = '—'

/**
 * Der Zeitraum der Steuerbeobachtung — dieselbe Grenze wie `betrieb_routes.MAX_TAGE`.
 * Der Test liest den Backend-Wert nach: ändert er sich dort, wird er rot.
 */
export const MAX_TAGE = 366

/** `legal_entity` — liegt in `beschriftungen.js` (auch die Detailseite braucht es); weitergereicht für den Test. */
export { RECHTSTRAEGER_LABEL }

/** Die drei Läufe des Tagesabschlusses (`betrieb_routes.LAUF_*`). */
export const LAUF_LABEL = {
  'cron-invoicing-wiederaufsetzer': 'Resumption',
  'cron-invoicing-abgleich': 'Reconciliation',
  // Der automatische BB-Upload (#3076). „BB" ist der Begriff, den die Seite überall
  // für BuchhaltungsButler führt („BB state", „Receipt chain (BB)"); „BB upload"
  // bleibt damit in ihrer Sprache statt eines neuen Worts.
  'cron-invoicing-bb-upload': 'BB upload',
}

/** `admin_sync_runs.status` (`notify/cron_runner.py`: 'running' → 'done' | 'error'). */
export const LAUF_STATUS_LABEL = { running: 'Running', done: 'Done', error: 'Failed' }
const LAUF_STATUS_TON = { running: 'info', done: 'ok', error: 'err' }

/**
 * Die eine Laufwarnung, die KEINE Störung meldet: bei `INVOICING_ISSUE_MODE=off`
 * ist ein fehlender Wiederaufsetzer-Lauf der erwartete Zustand (L7). Wörtlich wie
 * das Backend sie schreibt — der Test liest sie in `betrieb_routes.py` nach.
 */
export const KEIN_LAUF_ERWARTET = 'Ausstellung aus — kein Lauf erwartet'

/**
 * Die englische ANZEIGE des einen bekannten Hinweistexts (Auftrag Welle 3 PR 3,
 * Nachbesserung 2). Der Backend-Satz bleibt der VERGLEICHSWERT — `warnTon` und
 * `LaufHinweis` prüfen weiter gegen ihn —, angezeigt wird die englische Fassung,
 * damit die Seite nicht halb deutsch rendert. Andere `warnung`-Sätze kommen
 * wörtlich vom Backend und bleiben, wie sie sind.
 */
export const KEIN_LAUF_ERWARTET_ANZEIGE = 'Issuance off — no run expected'
export const zeigeWarnung = (warnung) => (
  warnung === KEIN_LAUF_ERWARTET ? KEIN_LAUF_ERWARTET_ANZEIGE : warnung
)

/** `tax_plausibility.SPERRE` / `HINWEIS`. */
export const SCHWERE_LABEL = { sperre: 'Block', hinweis: 'Notice' }
const SCHWERE_TON = { sperre: 'err', hinweis: 'warn' }

/** `product_group` — eine OFFENE Menge (`tax_case.py`); Unbekanntes steht roh da. */
const WARENGRUPPE_LABEL = { coffee: 'Coffee', equipment: 'Device', shipping: 'Shipping' }

// ── N5a — Zahlungsstörungen (Disputes/Chargebacks) ─────────────────────────

/** `invoice_dispute.art` (`dispute.ARTEN`) — deutsche Labels, keine rohen Codes. */
export const DISPUTE_ART_LABEL = { chargeback: 'Chargeback', inquiry: 'Inquiry' }

/**
 * Die sieben `DisputeStatus`-Werte (`dispute.STATUS_WERTE`) — englische Labels
 * (Admin-Text, kein Kundentext). `prevented` kam mit dem SDL-Neuzug 2026-10:
 * der Streit wurde verhindert, terminal und ohne Geldbewegung.
 * Ein unbekannter Wert wird gespeichert (kein CHECK auf `status`) und NICHT roh
 * gezeigt: er bekommt `DISPUTE_STATUS_UNBEKANNT` und einen Marker.
 */
export const DISPUTE_STATUS_LABEL = {
  accepted: 'Accepted',
  lost: 'Lost',
  needs_response: 'Response needed',
  under_review: 'Under review',
  prevented: 'Prevented',
  won: 'Won',
  charge_refunded: 'Refunded (superseded)',
}
export const DISPUTE_STATUS_UNBEKANNT = 'Status unknown'

// ── Formate ────────────────────────────────────────────────────────────────

const ganzzahl = (wert) => (Number.isFinite(wert) ? String(wert) : STRICH)
const betrag = (wert) => (Number.isFinite(wert)
  ? new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(wert)
  : STRICH)
/**
 * Ein Betrag mit Währung — de-DE. `betrag` kommt aus der API als String (`"119.00"`)
 * oder `null`; ohne Währung nur die Zahl, mit Währung das Symbol (`„119,00 €"`).
 * Nicht-parsebarer Wert → `null` (die Anzeige fällt auf „unbekannt" zurück, nie roh).
 */
const betragMitWaehrung = (wert, waehrung) => {
  const zahl = Number(wert)
  if (!Number.isFinite(zahl)) return null
  if (!waehrung) return betrag(zahl)
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: waehrung }).format(zahl)
  } catch (e) {
    return `${betrag(zahl)} ${waehrung}`
  }
}
/** `vat_rate` ist PROZENT (19), nicht der Anteil (Migration 326). */
const prozent = (wert) => (Number.isFinite(wert)
  ? `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(wert)} %`
  : STRICH)
const tagText = (iso) => formatBerlinDate(iso, { dateOnly: true })
const warengruppe = (wert) => (wert ? (WARENGRUPPE_LABEL[wert] || wert) : STRICH)

/** Eine Dauer in Sekunden, lesbar. `null` bleibt `null` — nie ein erfundenes „0 s". */
const dauer = (s) => {
  if (!Number.isFinite(s)) return null
  if (s < 120) return `${s} s`
  if (s < 7200) return `${Math.round(s / 60)} min`
  if (s < 172800) return `${Math.round(s / 3600)} h`
  return `${Math.round(s / 86400)} days`
}

// ── Eingaben prüfen — ungültig heißt: KEINE Anfrage ───────────────────────

/** `YYYY-MM-DD` → UTC-Millisekunden des Tages, sonst `null`. Ohne Zeitzone, ohne Sommerzeit. */
const alsTag = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '')
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null
}

/** Ein Fehlertext für den Zeitraum oder `null`. Die Grenze ist inklusiv wie im Backend. */
const pruefeZeitraum = (von, bis) => {
  const a = alsTag(von)
  const b = alsTag(bis)
  if (a === null || b === null) return 'Give From and To.'
  if (b < a) return 'To is before From.'
  const tage = (b - a) / 86400000 + 1
  if (tage > MAX_TAGE) return `At most ${MAX_TAGE} days — ${tage} days selected.`
  return null
}

// ── Lesen ──────────────────────────────────────────────────────────────────

const fehlertext = (e) => e?.response?.data?.error || e?.message || 'Unknown error'

/**
 * Ein Lesepfad mit Laden/Fehler/Neuladen. `params === null` heißt: die Eingabe
 * ist ungültig — es geht KEINE Anfrage hinaus, der letzte Stand bleibt stehen
 * (er trägt seinen eigenen Zeitraum bzw. Tag aus der Antwort).
 *
 * Ein Fehler löscht die Daten: eine Zahl, deren Abruf gerade scheiterte, darf
 * nicht als gültig stehen bleiben.
 */
function useLesen(pfad, params) {
  const schluessel = params === null ? null : JSON.stringify(params)
  const [stand, setStand] = useState({ daten: null, laedt: schluessel !== null, fehler: null })
  const [runde, setRunde] = useState(0)

  useEffect(() => {
    if (schluessel === null) {
      // Ungültige Eingabe: es geht nichts hinaus, und eine noch laufende Anfrage ist
      // abgemeldet (Aufräumen unten) — also lädt auch nichts mehr. Ohne das liefe die
      // Leiste „Wird aktualisiert" weiter, bis die Eingabe wieder gültig ist.
      setStand((alt) => (alt.laedt ? { ...alt, laedt: false } : alt))
      return undefined
    }
    let abgemeldet = false
    setStand((alt) => ({ ...alt, laedt: true, fehler: null }))
    api.get(pfad, { params: JSON.parse(schluessel) })
      .then(({ data }) => { if (!abgemeldet) setStand({ daten: data, laedt: false, fehler: null }) })
      .catch((e) => { if (!abgemeldet) setStand({ daten: null, laedt: false, fehler: fehlertext(e) }) })
    return () => { abgemeldet = true }
  }, [pfad, schluessel, runde])

  const neuLaden = useCallback(() => setRunde((n) => n + 1), [])
  return { ...stand, neuLaden }
}

// ── Bausteine dieser Seite ─────────────────────────────────────────────────

/**
 * Ein einklappbarer Abschnitt; `section` mit Namen ist eine Landmarke (`region`).
 *
 * 🔴 `tabIndex={-1}` macht den Abschnitt zum Ankerziel: `springeZu` scrollt ihn
 *    heran und setzt den Fokus, damit Tastatur und Screenreader ankommen. Ohne
 *    das wäre ein Sprung nur eine Scrollbewegung für die Maus.
 *
 * 🔴 **`scrollMarginTop` hält den Zielkopf UNTER der festen Kopfleiste.** Der
 *    Sprung setzt mit `block: 'start'` die Oberkante des Abschnitts auf die
 *    Oberkante des Viewports — und die Leiste liegt fest darüber. Die Summe
 *    `MOBILE_APP_BAR_CLEARANCE + SP.section` traegt BEIDE Breiten: am Telefon
 *    sind es die 45 px der mobilen Leiste plus der Sektionsabstand, auf dem
 *    Desktop deckt derselbe Wert die 64 px der Desktop-Leiste. Dieselbe Form
 *    wie `VendorAnschriften.jsx` (dort gemessen und begruendet).
 *
 * 🔴 **`'&[data-sprungziel="aktiv"]': focusRing()`** — der Sprung ruft `.focus()`
 *    PROGRAMMATISCH; der UA-Outline ist dann nicht gesetzt, und ein Maus-Nutzer
 *    saehe das Ziel nicht. Ueber den Helfer, kein Literal am Aufrufer
 *    (`pattern-library.md` §Fokusring).
 *
 * 🔴 **Der Ring haengt am SPRUNG, nicht am allgemeinen Fokus** (R15-W1,
 *    2026-11-01). Mit `'&:focus'` trug JEDER Abschnitt einen Rahmen, sobald ein
 *    gewoehnlicher Klick auf nicht fokussierbaren Inhalt den `tabindex="-1"`-
 *    Abschnitt fokussierte — ein Ring ohne Sprung, der wie ein Bedienelement
 *    aussah. Das Merkmal setzt allein `springeZu`, `blur` nimmt es zurueck.
 *
 *    🔴 **Das ist eine VARIANTE der Hausform, nicht die Hausform selbst**
 *    (R15-W2, 2026-11-01). Hausform ist deklarativ: React-Zustand setzt eine
 *    Prop, `onBlur` nimmt sie zurueck (`VendorAnschriften.jsx:261/:297-298/:311`,
 *    `SituationHeader.jsx:1622/:1665-1666/:1677`). Hier ist es imperativ, und
 *    zwar aus zwei Gruenden, die am Aufbau liegen: `springeZu` ist eine
 *    MODULFUNKTION ausserhalb des Baums und hat keinen Zugriff auf
 *    Komponenten-Zustand, und `Abschnitt` ist zustandslos — die Hausform
 *    verlangte, fuenf Zustandswerte durchzureichen. Der Attributname, der Wert
 *    und der Selektor sind woertlich Haus; nur der Mechanismus weicht ab. Wer
 *    die Form kopiert, kopiert das Merkmal — und weiss hier, warum es
 *    imperativ steht. Die gemeinsame Mechanik liegt in
 *    `utils/sprungziel.js` (`markiereSprungziel`), weil `InvoiceList.jsx`
 *    (`zuRefunds`, Paneel nach dem Reiterwechsel) sie ebenfalls braucht.
 *
 *    Das Attribut gehoert keiner React-Prop dieses Bausteins — React fasst nur
 *    seine eigenen Props an, und die Abschnitte stehen als feste JSX-Kinder
 *    ohne `key`/Bedingung, ihr DOM-Knoten ueberlebt einen Re-Render.
 */
function Abschnitt({
  titel,
  children,
  tooltip,
  ankerRef,
  hinweis = null,
  titleVariant,
  defaultExpanded = true,
  ankerLauf = 0,
}) {
  // 🔴 **Der Hinweis steht IM Titel, nicht in `action` und nicht in `summary`.**
  //    Der Titel ist die Tippfläche, die aufklappt, und er schrumpft und bricht um
  //    (`minWidth: 0` + `flex: '1 1 auto'` in `CollapsibleSection.jsx`);
  //    `action` wäre über `stopPropagation` eine eigene Tippfläche ohne
  //    Schrumpfschutz, `summary` rendert unter `sm` gar nicht. Kein neuer Prop am
  //    geteilten Baustein — der Knoten reicht (Präzedenz `ProductDetail.jsx`).
  // 🔴 `component="span"`: `variant="caption"` allein erzeugt ein `<p>` im `<h6>`.
  // 🔴 Der Mittelpunkt trennt Titel und Hinweis (R16 C2 b / R15 W3). Er steht als
  //    eigene Zeichenkette IM Kopf, nicht nur in der Doku: `Changelog`,
  //    `docs/features/invoicing.md`, die JSDoc von `CollapsibleSection` und
  //    Plan §1.6 versprechen ihn, und die Jest-Probe
  //    „der Kopf trennt Titel und Hinweis mit ‚·'" hält ihn fest.
  const kopf = hinweis ? (
    <>
      {titel}{' · '}
      <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
        {hinweis}
      </Typography>
    </>
  ) : titel
  return (
    <Box
      component="section"
      aria-label={titel}
      tabIndex={-1}
      ref={ankerRef}
      sx={{
        scrollMarginTop: (t) => `calc(${MOBILE_APP_BAR_CLEARANCE} + ${t.spacing(SP.section)})`,
        '&[data-sprungziel="aktiv"]': focusRing(),
      }}
    >
      {/* 🔴 **`ankerLauf` als `key` klappt auf, ohne `CollapsibleSection` anzufassen.**
          `defaultExpanded` wirkt nur beim ersten Rendern (`useState`); ein
          `key`-Wechsel montiert neu und liest ihn neu. Der Anker in ein KIND eines
          zugeklappten Abschnitts stünde sonst auf `visibility: hidden` und täte
          sichtbar nichts. Die geladenen Zustände liegen alle ÜBER diesem `key`
          (die `useLesen`-Hooks sitzen in `Betrieb`), die Neumontage verliert also
          keine Daten. 🔴 **Der Faltzustand der vier Kinder geht dabei verloren:**
          jedes Kind ist ein `Abschnitt` → `CollapsibleSection` mit eigenem
          `useState(defaultExpanded)`; wer „Tax monitoring" zugeklappt hatte und
          dann über das Band springt, findet es wieder offen. Das ist
          hingenommen — der Sprung soll genau EINEN Abschnitt öffnen, und ein
          gehobener Faltzustand wäre ein Zustand über dem `key` (R16 W4). */}
      <CollapsibleSection
        key={ankerLauf}
        title={kopf}
        tooltip={tooltip}
        titleVariant={titleVariant}
        defaultExpanded={defaultExpanded || ankerLauf > 0}
      >
        {children}
      </CollapsibleSection>
    </Box>
  )
}

/** Eine benannte Gruppe innerhalb eines Abschnitts (kein Heading: der Abschnitt ist schon h6). */
function Gruppe({ titel, children, tooltip }) {
  return (
    <Box role="group" aria-label={titel}>
      <Typography sx={{ fontSize: FS.base, fontWeight: 600, mb: SP.chip }}>
        {tooltip ? <Fachbegriff label={titel} begriff={tooltip} /> : titel}
      </Typography>
      <Stack spacing={SP.tight}>{children}</Stack>
    </Box>
  )
}

function Erklaerung({ children }) {
  // 🔴 R16 W3 (S9-Nachbesserung): die Erklärzeilen liefen bei 1440 px über die
  //    ganze Panelbreite (rund 1080 px), während das Formular darunter auf
  //    `CONTENT_WIDTH.prose` steht. Ein Satz, der breiter läuft als das, was er
  //    erklärt, liest sich als eigener Absatz. Der Deckel ist derselbe benannte
  //    Token wie am Empfänger-Raster — kein Literal (Regel 22, `raw_page_cap`).
  return (
    <Typography variant="caption"
                sx={{ display: 'block', color: 'text.secondary', maxWidth: CONTENT_WIDTH.prose }}>
      {children}
    </Typography>
  )
}

/**
 * Laden / Fehler / Daten eines Abschnitts — dieselbe Ordnung wie S5.4-F.
 * `kennung` benennt den Ladebereich (`<kennung>-laden`) für die Tests.
 *
 * `eingabeFehler`: die Eingabe des Abschnitts ist gerade ungültig. Dann geht
 * keine Anfrage hinaus — auch „Erneut versuchen" könnte keine schicken. Der
 * Knopf ist deshalb gesperrt, und der Hinweis sagt, was stattdessen hilft:
 * eine gültige Eingabe lädt von selbst.
 */
function Zustand({ stand, kennung, fehlerTitel, eingabeFehler = null, children }) {
  if (stand.fehler) {
    return (
      <Alert
        severity="error"
        action={(
          <Button color="inherit" size="small" onClick={stand.neuLaden} disabled={Boolean(eingabeFehler)}>
            Try again
          </Button>
        )}
      >
        <AlertTitle>{fehlerTitel}</AlertTitle>
        {stand.fehler}
        {eingabeFehler && (
          <Box component="span" sx={{ display: 'block', mt: SP.chip }}>
            With the current input no request goes out ({eingabeFehler}) — a valid
            input reloads by itself.
          </Box>
        )}
      </Alert>
    )
  }
  if (stand.daten === null) {
    if (!stand.laedt) return null
    // Erstes Laden: Skeletons, kein Spinner (Anti-Pattern 11). `elevation={0}` wie
    // die Tabellen, damit der Grund beim Wechsel nicht springt.
    return (
      <Paper elevation={0} sx={PANEL_SX} data-testid={`${kennung}-laden`} aria-busy="true">
        <Stack spacing={SP.tight}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} data-testid="betrieb-skelett" variant="rounded" height={32} />
          ))}
        </Stack>
      </Paper>
    )
  }
  return (
    <Box aria-busy={stand.laedt ? 'true' : 'false'}>
      {stand.laedt && <LinearProgress aria-label="Updating" sx={{ mb: SP.chip }} />}
      {children(stand.daten, stand.neuLaden)}
    </Box>
  )
}

/**
 * Tabelle (Desktop) oder Kartenliste (≤ 600 px), aus EINER Spaltenbeschreibung —
 * Muster „Data Table / List" (`pattern-library.md`: bifurcate, Karten auf `xs`).
 * Beide Flächen tragen `elevation={0}`: die Pills darauf sind auf diesen Grund
 * gemessen. Die Karten sind eine benannte Liste, die Tabelle trägt denselben Namen.
 *
 * `spalten`: `[{ key, titel, wert: (zeile) => ReactNode, rechts? }]`
 */
function DatenTabelle({ name, spalten, zeilen, schmal, schluessel }) {
  if (schmal) {
    return (
      <Stack spacing={SP.tight} role="list" aria-label={name}>
        {zeilen.map((z, i) => (
          <Card key={schluessel(z, i)} elevation={0} sx={PANEL_SX} role="listitem">
            <CardContent>
              <Stack spacing={SP.chip}>
                {spalten.map((s) => (
                  <Stack key={s.key} direction="row" justifyContent="space-between" alignItems="baseline" gap={SP.sm}>
                    <Typography component="span" sx={{ fontSize: FS.sm, color: 'text.secondary', flexShrink: 0 }}>
                      {s.tooltip ? <Fachbegriff label={s.titel} begriff={s.tooltip} /> : s.titel}
                    </Typography>
                    <Box component="span" sx={{ fontSize: FS.sm, textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere' }}>
                      {s.wert(z)}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    )
  }
  return (
    // Breite Tabellen zwischen 600 und 960 px scrollen in ihrem Paper, nicht die Seite.
    <Paper elevation={0} sx={{ ...PANEL_SX, overflowX: 'auto' }}>
      <Table size="small" aria-label={name}>
        <TableHead>
          <TableRow>
            {spalten.map((s) => (
              <TableCell key={s.key} align={s.rechts ? 'right' : 'left'}>
                {s.tooltip ? <Fachbegriff label={s.titel} begriff={s.tooltip} /> : s.titel}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Kein `hover`: die Zeilen selbst sind nicht klickbar — Verweise stehen
              IN den Zellen (Rechnungs-ID, Bestellung). Kein `stopPropagation`
              noetig: `<TableRow>` traegt keinen `onClick`. */}
          {zeilen.map((z, i) => (
            <TableRow key={schluessel(z, i)}>
              {spalten.map((s) => (
                <TableCell key={s.key} align={s.rechts ? 'right' : 'left'} sx={{ fontSize: FS.sm }}>
                  {s.wert(z)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  )
}

const Gedaempft = ({ children, ...rest }) => (
  <Typography component="span" {...rest} sx={{ color: 'text.secondary', fontSize: 'inherit' }}>{children}</Typography>
)

const BetriebPill = ({ tone, children }) => (
  <Pill tone={tone} bordered nowrap data-testid="betrieb-pill">{children}</Pill>
)

/** Die Rechnungs-ID als Link aufs Detail (S5.5-F) — die API liefert hier nur die ID. */
const RechnungsLink = ({ id }) => (
  <Link component={RouterLink} to={`/invoicing/${id}`}>ID {id}</Link>
)

/**
 * Was mit der Rechnungsmail WIRKLICH geschah (R16 B1, beide Wege der Handrechnung).
 * `mail` ist das Ergebnis des Versands, `mail_grund` der Zustand aus `mailing`.
 * Ein unbekannter oder fehlender Grund heisst „nicht versandt" — nie „unterdrückt":
 * `null` kommt aus einem gefangenen Fehler im Versand.
 */
const MailErgebnis = ({ erfolg }) => {
  if (erfolg.mail === true) {
    return <Box sx={{ mt: SP.chip }}>The invoice was mailed.</Box>
  }
  if (erfolg.mail !== false) return null
  if (erfolg.mail_grund === 'unterdrueckt') {
    return <Box sx={{ mt: SP.chip }}>No mail — the automatic delivery is suppressed.</Box>
  }
  if (erfolg.mail_grund === 'ohne_empfaenger') {
    return <Box sx={{ mt: SP.chip }}>No mail delivery — no recipient address is stored.</Box>
  }
  return (
    <Box sx={{ mt: SP.chip }}>
      No mail delivery — the delivery failed or is blocked. Check in the invoice detail and resend there.
      {erfolg.mail_grund ? <Gedaempft>{` (${erfolg.mail_grund})`}</Gedaempft> : null}
    </Box>
  )
}

// ── (a) Nummernkreise ──────────────────────────────────────────────────────

const KREIS_SPALTEN = [
  { key: 'art', titel: 'Document type', wert: (z) => ART_LABEL[z.document_type] || z.document_type || STRICH },
  { key: 'traeger', titel: 'Legal entity', tooltip: 'Rechtsträger', wert: (z) => RECHTSTRAEGER_LABEL[z.legal_entity] || z.legal_entity || STRICH },
  { key: 'jahr', titel: 'Year', wert: (z) => ganzzahl(z.jahr) },
  { key: 'praefix', titel: 'Prefix', wert: (z) => z.praefix || STRICH },
  {
    key: 'letzte',
    titel: 'Last number',
    // 🔴 `null` heißt: noch keine Nummer vergeben. Die Nullnummer gibt es nicht —
    //    eine „0" oder „RE2027-00000" wäre eine erfundene Nummer.
    wert: (z) => z.letzte_nummer || <Gedaempft>no number yet</Gedaempft>,
  },
  { key: 'vergeben', titel: 'Issued', rechts: true, wert: (z) => ganzzahl(z.vergeben) },
  // Die Zahl bleibt stehen, auch negativ — `auffaellig` daneben ist das Urteil.
  { key: 'luecke', titel: 'Gap', rechts: true, wert: (z) => ganzzahl(z.luecke) },
  {
    key: 'pruefung',
    titel: 'Check',
    wert: (z) => (z.auffaellig
      ? <BetriebPill tone="err">Flagged</BetriebPill>
      : <BetriebPill tone="ok">Unflagged</BetriebPill>),
  },
  {
    key: 'folgejahr',
    titel: 'Next-year range',
    wert: (z) => (z.folgejahr_besteht ? 'created' : <Gedaempft>not created yet</Gedaempft>),
  },
]

function Nummernkreise({ stand, schmal }) {
  return (
    <Zustand stand={stand} kennung="nummernkreise" fehlerTitel="The number ranges could not be loaded.">
      {(daten) => {
        const kreise = daten?.kreise || []
        if (kreise.length === 0) {
          return (
            <Alert severity="info">
              <AlertTitle>No number range yet.</AlertTitle>
              A range is created with the first issued number.
            </Alert>
          )
        }
        return (
          <Stack spacing={SP.tight}>
            <DatenTabelle
              name="Number ranges"
              spalten={KREIS_SPALTEN}
              zeilen={kreise}
              schmal={schmal}
              schluessel={(z) => `${z.document_type}-${z.legal_entity}-${z.jahr}`}
            />
            <Erklaerung>
              Gap = last number − issued numbers; 0 after every issuance. “Flagged” means:
              gap not equal to 0, in both directions. The range of a year is created with its first number.
            </Erklaerung>
          </Stack>
        )
      }}
    </Zustand>
  )
}

// ── (b) Steuerbeobachtung ──────────────────────────────────────────────────

const MATRIX_SPALTEN = [
  { key: 'land', titel: 'Delivery country', wert: (z) => z.lieferland || STRICH },
  { key: 'gruppe', titel: 'Product group', wert: (z) => warengruppe(z.warengruppe) },
  { key: 'satz', titel: 'Rate', rechts: true, wert: (z) => prozent(z.satz) },
  { key: 'anzahl', titel: 'Items', rechts: true, wert: (z) => ganzzahl(z.anzahl) },
  { key: 'brutto', titel: 'Gross total', rechts: true, wert: (z) => betrag(z.summe_brutto) },
  { key: 'steuer', titel: 'Tax total', rechts: true, wert: (z) => betrag(z.summe_steuer) },
]

const ABWEICHUNG_SPALTEN = [
  {
    key: 'schwere',
    titel: 'Severity',
    wert: (z) => (
      <BetriebPill tone={SCHWERE_TON[z.schwere] || 'plain'}>{SCHWERE_LABEL[z.schwere] || z.schwere || STRICH}</BetriebPill>
    ),
  },
  // Der Befundtext kommt wörtlich aus `tax_plausibility` — die Seite urteilt nicht selbst.
  { key: 'text', titel: 'Finding', wert: (z) => z.text || z.grund || STRICH },
  { key: 'rechnung', titel: 'Invoice', wert: (z) => <RechnungsLink id={z.invoice_id} /> },
  { key: 'land', titel: 'Delivery country', wert: (z) => z.lieferland || STRICH },
  { key: 'gruppe', titel: 'Product group', wert: (z) => warengruppe(z.warengruppe) },
  { key: 'satz', titel: 'Rate', rechts: true, wert: (z) => prozent(z.satz) },
  // `null`, wenn die Erwartung eine Menge ist (`tax_plausibility.TaxWarning`).
  { key: 'erwartet', titel: 'Expected', rechts: true, wert: (z) => prozent(z.erwartet) },
]

const SPERRE_SPALTEN = [
  { key: 'nummer', titel: 'Invoice', wert: (z) => z.nummer || <Gedaempft>without number</Gedaempft> },
  { key: 'id', titel: 'ID', wert: (z) => <RechnungsLink id={z.invoice_id} /> },
  {
    key: 'grund',
    titel: 'Reason',
    wert: (z) => <Box component="code" sx={{ fontFamily: 'monospace', fontSize: FS.sm }}>{z.grund}</Box>,
  },
  { key: 'anzahl', titel: 'Count', rechts: true, wert: (z) => ganzzahl(z.anzahl) },
  { key: 'erstes', titel: 'First', wert: (z) => formatBerlinDateTime(z.erstes) },
  { key: 'letztes', titel: 'Last', wert: (z) => formatBerlinDateTime(z.letztes) },
]

// N12 §2.5 — nicht-steuerliche Sperren tragen einen deutschen Satz (`text`); der
// rohe Kurzcode bleibt daneben stehen, damit die Zuordnung nachvollziehbar ist.
// 🔴 Der Spaltenkopf heisst „Beschreibung", nicht „Grund" (R16 B1): die
// Nachbartabelle „Steuersperren" trägt unter „Grund" den **rohen Code**, hier
// steht der **Satz** — zwei gleich beschriftete Spalten mit verschiedener
// Datenart stolpern beim Nebeneinanderlesen.
const WEITERE_SPERRE_SPALTEN = [
  { key: 'nummer', titel: 'Invoice', wert: (z) => z.nummer || <Gedaempft>without number</Gedaempft> },
  { key: 'id', titel: 'ID', wert: (z) => <RechnungsLink id={z.invoice_id} /> },
  { key: 'text', titel: 'Description', wert: (z) => z.text || z.grund || STRICH },
  {
    key: 'grund',
    titel: 'Code',
    wert: (z) => <Box component="code" sx={{ fontFamily: 'monospace', fontSize: FS.sm }}>{z.grund}</Box>,
  },
  { key: 'anzahl', titel: 'Count', rechts: true, wert: (z) => ganzzahl(z.anzahl) },
  { key: 'erstes', titel: 'First', wert: (z) => formatBerlinDateTime(z.erstes) },
  { key: 'letztes', titel: 'Last', wert: (z) => formatBerlinDateTime(z.letztes) },
]

function Steuerbeobachtung({ stand, schmal, von, bis, setVon, setBis, fehler }) {
  // Der Hinweis unter „Bis" gilt dem ZEITRAUM, also beiden Feldern: „Von" verweist
  // auf denselben Text (WCAG 1.3.1/3.3.1) — sonst trüge es `aria-invalid` ohne Grund.
  const bisId = useId()
  const zeitraumHinweisId = `${bisId}-helper-text`
  return (
    <Stack spacing={SP.card}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={SP.tight} alignItems={{ sm: 'flex-start' }}>
        <TextField
          size="small"
          type="date"
          label="From"
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'aria-describedby': zeitraumHinweisId }}
          value={von}
          onChange={(e) => setVon(e.target.value)}
          error={Boolean(fehler)}
          helperText=" "
        />
        <TextField
          id={bisId}
          size="small"
          type="date"
          label="To"
          InputLabelProps={{ shrink: true }}
          value={bis}
          onChange={(e) => setBis(e.target.value)}
          error={Boolean(fehler)}
          helperText={fehler || `At most ${MAX_TAGE} days`}
        />
      </Stack>
      <Zustand
        stand={stand}
        kennung="steuerbeobachtung"
        fehlerTitel="The tax monitoring could not be loaded."
        eingabeFehler={fehler}
      >
        {(daten) => {
          const matrix = daten?.matrix || []
          const abweichungen = daten?.abweichungen || []
          const sperren = daten?.sperren || []
          const weitere = daten?.sperren_weitere || []
          return (
            <Stack spacing={SP.md}>
              <Erklaerung>
                Shown: {tagText(daten?.von)} – {tagText(daten?.bis)}. Invoices only, no corrections.
                Matrix and deviations by invoice date (= shipment date), blocks by the Berlin
                calendar day of the attempt.
              </Erklaerung>

              <Gruppe titel="Rate matrix">
                {matrix.length > 0 ? (
                  <>
                    <DatenTabelle
                      name="Rate matrix"
                      spalten={MATRIX_SPALTEN}
                      zeilen={matrix}
                      schmal={schmal}
                      schluessel={(z, i) => `${z.lieferland}-${z.warengruppe}-${z.satz}-${i}`}
                    />
                    <Erklaerung>
                      Totals: amounts of the documents (Forge, frozen), without currency. Items:
                      number of invoice items.
                    </Erklaerung>
                  </>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No invoice with items was issued in the period.
                  </Typography>
                )}
              </Gruppe>

              <Gruppe titel="Deviations">
                {abweichungen.length > 0 ? (
                  <DatenTabelle
                    name="Deviations"
                    spalten={ABWEICHUNG_SPALTEN}
                    zeilen={abweichungen}
                    schmal={schmal}
                    schluessel={(z, i) => `${z.invoice_id}-${z.grund}-${i}`}
                  />
                ) : (
                  // 🔴 „Keine Abweichung" über NULL geprüfte Rechnungen ist „nicht
                  //    hingesehen", nicht „nichts gefunden" (Regel 17 §3b).
                  <Alert severity="info">
                    {matrix.length > 0
                      ? 'No deviation in the period.'
                      : 'No deviation in the period — no invoice was available that could be checked.'}
                  </Alert>
                )}
              </Gruppe>

              <Gruppe titel="Tax blocks">
                {sperren.length > 0 ? (
                  <>
                    <DatenTabelle
                      name="Tax blocks"
                      spalten={SPERRE_SPALTEN}
                      zeilen={sperren}
                      schmal={schmal}
                      schluessel={(z) => `${z.invoice_id}-${z.grund}`}
                    />
                    <Erklaerung>
                      Blocks with a “steuer:” reason, grouped per invoice and reason — the reason why
                      an invoice was not issued.
                    </Erklaerung>
                  </>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No tax block in the period.
                  </Typography>
                )}
              </Gruppe>

              <Gruppe titel="Other blocks">
                {weitere.length > 0 ? (
                  <>
                    <DatenTabelle
                      name="Other blocks"
                      spalten={WEITERE_SPERRE_SPALTEN}
                      zeilen={weitere}
                      schmal={schmal}
                      schluessel={(z) => `${z.invoice_id}-${z.grund}`}
                    />
                    <Erklaerung>
                      Blocks without a “steuer:” reason, grouped per invoice and reason — the reason
                      stands as a sentence, because the short code alone does not make it readable.
                    </Erklaerung>
                  </>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No other block in the period.
                  </Typography>
                )}
              </Gruppe>
            </Stack>
          )
        }}
      </Zustand>
    </Stack>
  )
}

// ── (c) Tagesabschluss ─────────────────────────────────────────────────────

const VERTEILUNG_SPALTEN = [
  { key: 'art', titel: 'Dokumentart', wert: (z) => ART_LABEL[z.art] || z.art || STRICH },
  {
    key: 'zustand',
    titel: 'BB state',
    wert: (z) => (z.bb_state ? (BB_LABEL[z.bb_state] || z.bb_state) : <Gedaempft>no BB state</Gedaempft>),
  },
  { key: 'anzahl', titel: 'Count', rechts: true, wert: (z) => ganzzahl(z.anzahl) },
]

/** Ton und Beschriftung einer Laufwarnung — am STATUS und am bekannten Hinweistext, nie geraten. */
const warnTon = (z) => {
  if (z.status === 'error') return ['err', 'Error']
  if (z.warnung === KEIN_LAUF_ERWARTET) return ['info', 'Notice']
  return ['warn', 'Warning']
}

const LAUF_SPALTEN = [
  { key: 'lauf', titel: 'Run', wert: (z) => LAUF_LABEL[z.kind] || z.kind || STRICH },
  {
    key: 'status',
    titel: 'Status',
    wert: (z) => (z.status
      ? <BetriebPill tone={LAUF_STATUS_TON[z.status] || 'plain'}>{LAUF_STATUS_LABEL[z.status] || z.status}</BetriebPill>
      : <BetriebPill tone="plain">No run</BetriebPill>),
  },
  {
    key: 'start',
    titel: 'Last start',
    wert: (z) => {
      if (!z.gestartet) return <Gedaempft>no run</Gedaempft>
      const alter = dauer(z.alter_sekunden)
      return alter ? `${formatBerlinDateTime(z.gestartet)} · ${alter} ago` : formatBerlinDateTime(z.gestartet)
    },
  },
  { key: 'takt', titel: 'Cadence', wert: (z) => (dauer(z.takt_sekunden) ? `every ${dauer(z.takt_sekunden)}` : STRICH) },
  {
    key: 'warnung',
    titel: 'Warning',
    wert: (z) => {
      if (!z.warnung) return <Gedaempft>none</Gedaempft>
      const [ton, label] = warnTon(z)
      return (
        <Stack direction="row" gap={SP.chip} alignItems="center" flexWrap="wrap" useFlexGap
               justifyContent={{ xs: 'flex-end', sm: 'flex-start' }}>
          <BetriebPill tone={ton}>{label}</BetriebPill>
          <Typography component="span" sx={{ fontSize: 'inherit' }}>{zeigeWarnung(z.warnung)}</Typography>
        </Stack>
      )
    },
  },
]

// ── N7: der gespeicherte BB-Abgleich und die Shopify-Seite ─────────────────
// Jede Menge hier ist in `__tests__/Betrieb.test.jsx` gegen ihre Quelle gelesen:
// die Klassen gegen `bb_zustand.ABGLEICH_KLASSEN` / `ABGLEICH_NICHT_ENTSCHEIDEND`,
// die Ergebnisse gegen den CHECK aus Migration 339, die Sweeper-Zahlen gegen
// `outbox.ERGEBNISSE` + `_kennzahlen()` (dazu `issuance.GRUND_VOR_FORMWECHSEL`).
// Eine neue Klasse oder ein neues Ergebnis macht den Test rot, nicht die Seite still.

/** Die drei Klassen des Abgleich-Vorspanns (`bb_zustand.ABGLEICH_KLASSEN`). */
export const ABGLEICH_KLASSE_LABEL = {
  forge_ohne_bb: 'Forge without BB receipt',
  bb_ohne_forge: 'BB without Forge invoice',
  betragsabweichung: 'Amount deviation',
}

/**
 * Die Klasse, die gezählt wird, das Ergebnis aber NICHT entscheidet
 * (`bb_zustand.ABGLEICH_NICHT_ENTSCHEIDEND`): ob ein BB-Beleg ohne Forge-Rechnung
 * eine fehlende Rechnung oder ein Lexware-Beleg ist, ist nicht entscheidbar.
 */
export const ABGLEICH_NICHT_ENTSCHEIDEND = ['bb_ohne_forge']

/** Die drei Ergebnisse (`invoice_bb_abgleich.ergebnis`, CHECK aus Migration 339). */
export const ABGLEICH_ERGEBNIS_LABEL = {
  stimmig: 'Consistent',
  abweichung: 'Deviation',
  nicht_gemessen: 'Not measured',
}

/**
 * Die Zahlen eines Sweeper-Laufs — `outbox._kennzahlen()`: jeder Wert aus
 * `outbox.ERGEBNISSE` und `gesamt`, in der Reihenfolge der Kacheln. Es gibt
 * KEIN `erzeugt` (R15-B2/R16-B1: die Seite war gegen einen erfundenen Vertrag gebaut).
 */
export const SWEEPER_ZAHL_LABEL = {
  gesamt: 'Total',
  ausgestellt: 'Issued',
  gesperrt: 'Blocked',
  schon_erledigt: 'Already resolved',
  uebersprungen: 'Skipped',
  fehler: 'Error',
  nur_signal: 'Signal only',
  vor_start: 'Before the cut-off',
  refund_entschieden: 'Refund decided',
  refund_leerlauf: 'Refund without effect',
}

/**
 * Zahlen, die nur der Outbox-VERBRAUCHER setzt — der Sweeper zählt sie nie
 * (`outbox.sweeper`). Sie stehen nur da, wenn sie doch einmal nicht 0 sind:
 * dann ist das ein Befund, kein Rauschen.
 */
export const SWEEPER_NUR_VERBRAUCHER = ['nur_signal', 'vor_start', 'refund_entschieden', 'refund_leerlauf']

/**
 * Die Unterzahl von „Übersprungen" (`issuance.GRUND_VOR_FORMWECHSEL`). Sie zählt
 * NICHT in „Gesamt" und steht deshalb als „davon"-Hinweis, nicht als eigene Kachel.
 */
export const SWEEPER_UNTERZAHL = 'vor_formwechsel'

/**
 * Eine Untergruppe innerhalb einer `Gruppe` — als Paneel abgesetzt (dasselbe
 * `PANEL_SX` wie Tabellen und Karten dieser Seite), Titel eine Stufe kleiner.
 * So liest sie sich als TEIL der umgebenden Gruppe, nicht als Nachbargruppe (R16-W4).
 */
function UnterGruppe({ titel, children }) {
  return (
    <Paper elevation={0} sx={PANEL_SX} role="group" aria-label={titel}>
      <Typography sx={{ fontSize: FS.sm, fontWeight: 600, color: 'text.secondary', mb: SP.chip }}>
        {titel}
      </Typography>
      <Stack spacing={SP.tight}>{children}</Stack>
    </Paper>
  )
}

/**
 * Die Warnung eines gespeicherten Laufs: Befund und Ursache aus der Antwort, darunter
 * der Handgriff; ein Befehl steht als Code (R16-B3 — keine Markdown-Backticks im Text).
 * Der Text „kein Lauf erwartet" ist ein HINWEIS, keine Warnung — derselbe Ton wie in
 * der Läufe-Tabelle (`warnTon`, R16-W1).
 */
function LaufHinweis({ warnung, handgriff, befehl }) {
  if (!warnung) return null
  return (
    <Alert severity={warnung === KEIN_LAUF_ERWARTET ? 'info' : 'warning'} role="status">
      {zeigeWarnung(warnung)}
      {handgriff && (
        <Box component="span" sx={{ display: 'block', mt: SP.chip }}>
          Manual step: {handgriff}
          {befehl && (
            <Box
              component="code"
              sx={{ display: 'block', fontFamily: 'monospace', fontSize: FS.sm, overflowWrap: 'anywhere' }}
            >
              {befehl}
            </Box>
          )}
        </Box>
      )}
    </Alert>
  )
}

/** Fehlt ein Teil der Antwort ganz, sagt die Seite DAS — nicht „nicht gemessen" (R16-W5). */
const AntwortOhne = ({ children }) => (
  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{children}</Typography>
)

/**
 * „Letzter Abgleich" (N7) — Stand, Alter, Fenster, die drei Klassen, die Warnung.
 * Eine Untergruppe der „Belegstrecke (BB)".
 *
 * 🔴 **Ohne Zeile: „nicht gemessen", nie 0** (Regel 17 §3b). Eine 0 sähe aus wie
 *    „abgeglichen, nichts gefunden" — die zwei sind nicht dasselbe.
 * 🔴 **EINE Aussage je Bereich**: trägt die Antwort eine Warnung, sagt sie den Satz
 *    selbst — der eigene Leertext daneben wäre dieselbe Zeile ein zweites Mal.
 */
function LetzterAbgleich({ ab }) {
  if (!ab) {
    return (
      <UnterGruppe titel="Last reconciliation">
        <AntwortOhne>The response names no reconciliation.</AntwortOhne>
      </UnterGruppe>
    )
  }
  const vorhanden = ab.vorhanden === true
  const alter = dauer(ab.alter_sekunden)
  const fenster = ab.fenster_von && ab.fenster_bis
    ? `Compared were the Forge invoices from ${tagText(ab.fenster_von)} to ${tagText(ab.fenster_bis)} with the BB stock.`
    : 'Forge was empty during the run — there was no window to compare.'
  return (
    <UnterGruppe titel="Last reconciliation">
      {vorhanden && (
        <Kacheln kacheln={[
          ...Object.entries(ABGLEICH_KLASSE_LABEL).map(([key, label]) => ({
            label,
            value: ganzzahl(ab.klassen?.[key]),
            hint: ABGLEICH_NICHT_ENTSCHEIDEND.includes(key) ? 'not decisive' : 'last reconciliation',
          })),
          {
            label: 'Result',
            value: ABGLEICH_ERGEBNIS_LABEL[ab.ergebnis] || ab.ergebnis || STRICH,
            hint: ab.lauf_ende
              ? `${formatBerlinDateTime(ab.lauf_ende)}${alter ? ` · ${alter} ago` : ''}`
              : undefined,
          },
        ]} />
      )}
      {!vorhanden && !ab.warnung && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No BB reconciliation stored yet — not measured. Deliberately no 0 here.
        </Typography>
      )}
      <LaufHinweis warnung={ab.warnung} handgriff={ab.handgriff} befehl={ab.befehl} />
      <Erklaerung>
        {vorhanden && <>{fenster}{' '}</>}
        The numbers belong to the last stored run, not to the selected day. “BB without
        Forge invoice” is only counted: whether such a receipt is a missing invoice or a
        Lexware receipt cannot be decided — it does not make the result a deviation.
      </Erklaerung>
    </UnterGruppe>
  )
}

/**
 * Die Zahl-Kacheln eines Sweeper-Laufs — nur die Menge, die der Sweeper zählt.
 * Gibt `[kacheln, unbeschriftet]` zurück: `unbeschriftet` ist die Zahl der Schlüssel,
 * die die Seite nicht kennt (sie stehen NICHT als roher Name da, R15-B2).
 */
const sweeperKacheln = (zahlen) => {
  const kacheln = Object.entries(SWEEPER_ZAHL_LABEL)
    .filter(([key]) => key in zahlen)
    .filter(([key]) => !(SWEEPER_NUR_VERBRAUCHER.includes(key) && zahlen[key] === 0))
    .map(([key, label]) => ({
      label,
      value: ganzzahl(zahlen[key]),
      hint: key === 'uebersprungen' && Number.isFinite(zahlen[SWEEPER_UNTERZAHL])
        ? `of which before the change of form: ${zahlen[SWEEPER_UNTERZAHL]}`
        : 'last run',
    }))
  const unbeschriftet = Object.keys(zahlen)
    .filter((key) => !(key in SWEEPER_ZAHL_LABEL) && key !== SWEEPER_UNTERZAHL).length
  return [kacheln, unbeschriftet]
}

/**
 * Die Shopify-Seite (N7): der letzte Sweeper-Lauf samt Zahlen aus dem Laufprotokoll.
 *
 * 🔴 **Ohne Zeile: „nicht gemessen", nie 0** — dieselbe Regel wie beim Abgleich,
 *    und dieselbe „EINE Aussage"-Regel (R16-B2).
 */
function LetzterSweeper({ sh }) {
  const letzter = sh?.letzter_sweeper
  if (!letzter) {
    return (
      <Gruppe titel="Shopify — last sweeper run">
        <AntwortOhne>The response names no sweeper run.</AntwortOhne>
      </Gruppe>
    )
  }
  const vorhanden = letzter.vorhanden === true
  const [zahlKacheln, unbeschriftet] = sweeperKacheln(letzter.zahlen || {})
  const alter = dauer(letzter.alter_sekunden)
  return (
    <Gruppe titel="Shopify — last sweeper run">
      {/* 🔴 S8: der Momentaufnahme-Hinweis steht EINMAL am Abschnittskopf
          (`Tagesabschluss`), nicht je Gruppe. */}
      {vorhanden && (
        <Kacheln kacheln={[
          {
            label: 'Last run',
            value: formatBerlinDateTime(letzter.ran_at),
            hint: alter ? `${alter} ago` : undefined,
          },
          ...zahlKacheln,
        ]} />
      )}
      {!vorhanden && !letzter.warnung && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No sweeper run recorded — not measured. Deliberately no 0 here.
        </Typography>
      )}
      <LaufHinweis warnung={letzter.warnung} handgriff={letzter.handgriff} befehl={letzter.befehl} />
      {unbeschriftet > 0 && (
        <Erklaerung>
          The response names {unbeschriftet === 1 ? 'one more number' : `${unbeschriftet} more numbers`} without
          a label — this page does not know them yet.
        </Erklaerung>
      )}
      <Erklaerung>
        The last Shopify sweeper run from the run log. It catches up mailed orders
        that the outbox missed.
      </Erklaerung>
    </Gruppe>
  )
}

/**
 * Sichtbar, sobald ein Bereich „jetzt" misst, der Tag aber nicht heute ist
 * (vergangen oder künftig).
 *
 * 🔴 R16 W2 / R15: der Satz nennt seinen GEGENSTAND, nicht mehr „this area".
 *    Seit S8 steht der Hinweis einmal über dem ganzen Körper von „Daily close",
 *    und zwei Gruppen dieses Abschnitts (Forge, Refund decisions) fragen sehr
 *    wohl einen Tag. „This area does not query a day" wäre über ihnen falsch.
 */
function Momentaufnahme({ tag }) {
  return (
    <Alert severity="info" role="status">
      Receipt chain, sweeper and runs: snapshot now, not {tagText(tag)} — these
      parts do not query a day.
    </Alert>
  )
}

function Kacheln({ kacheln }) {
  return (
    <KpiRow>
      {kacheln.map((k) => (
        <KpiCell key={k.label}>
          {/* Die Hülle trägt nur die Testkennung: die Tests lesen Beschriftung, Wert und
              Hinweis JE Kachel — sonst bliebe eine vertauschte Zahl grün. */}
          <Box data-testid="betrieb-kachel">
            <StatTile label={k.label} value={k.value} hint={k.hint} reserveHint />
          </Box>
        </KpiCell>
      ))}
    </KpiRow>
  )
}

/**
 * N5a — die Zahlungsstörungen: offene Disputes/Chargebacks mit Schließen.
 *
 * Ein Chargeback ist **kein** Korrekturdokument; er wird von Hand geschlossen
 * (mit Urheber, wie N6). `betrag` heisst „unbekannt" (nie 0), wenn der Dispute nur
 * über `order.disputes` gesehen wurde — dann steht auch `zugestellt: false`.
 */
function Zahlungsstoerungen({ stoerungen, schmal, neuLaden }) {
  const [zeile, setZeile] = useState(null)
  const [meldung, setMeldung] = useState(null)
  const [fehler, setFehler] = useState(null)
  const [sendet, setSendet] = useState(false)

  const schliesse = () => {
    if (!zeile || sendet) return
    setSendet(true)
    setFehler(null)
    const geschlossen = zeile.dispute_id
    api.post(`/api/invoicing/admin/disputes/${geschlossen}/schliessen`)
      .then(() => {
        setSendet(false)
        setZeile(null)
        setMeldung(`Payment disruption ${geschlossen} closed.`)
        neuLaden()
      })
      .catch((err) => {
        setSendet(false)
        if (err?.response?.status === 409) {
          setZeile(null)
          setMeldung(`Payment disruption ${geschlossen} was no longer open — the list was reloaded.`)
          neuLaden()
          return
        }
        setFehler(fehlertext(err))
      })
  }

  const spalten = [
    {
      key: 'rechnung',
      titel: 'Invoice',
      wert: (z) => (z.invoice_id != null
        ? <RechnungsLink id={z.invoice_id} />
        : <Gedaempft>without invoice</Gedaempft>),
    },
    // S4b: `wert` liefert ReactNode (Docstring `DatenTabelle`) — die Nachbarspalte
    // „Invoice" gibt bereits `<RechnungsLink/>` zurueck. Kein Sortier- und kein
    // CSV-Weg liest `wert` als Zeichenkette; deshalb kein zweites Feld.
    { key: 'bestellung', titel: 'Order', wert: (z) => <BestellLink eintrag={z} /> },
    { key: 'art', titel: 'Type', wert: (z) => DISPUTE_ART_LABEL[z.art] || z.art },
    {
      key: 'status',
      titel: 'Status',
      // Ein unbekannter Wert wird gespeichert und NICHT roh gezeigt: deutscher Satz
      // plus Marker (Regel 16 — kein roher Code).
      wert: (z) => (z.status_unbekannt
        ? <>{DISPUTE_STATUS_UNBEKANNT} <BetriebPill tone="warn">unknown</BetriebPill></>
        : (DISPUTE_STATUS_LABEL[z.status] || DISPUTE_STATUS_UNBEKANNT)),
    },
    {
      key: 'betrag',
      titel: 'Amount',
      rechts: true,
      // 🔴 de-DE mit Währung (R16 W2): `betrag` ist NULL bis N5b — dann „unbekannt",
      // nie roh und nie 0.
      wert: (z) => {
        const text = z.betrag != null ? betragMitWaehrung(z.betrag, z.waehrung) : null
        return text != null ? text : <Gedaempft>unknown</Gedaempft>
      },
    },
    { key: 'frist', titel: 'Deadline', wert: (z) => (z.frist ? tagText(z.frist) : STRICH) },
    {
      key: 'zugestellt',
      titel: 'Delivered',
      wert: (z) => (z.zugestellt ? 'yes' : <Gedaempft>no</Gedaempft>),
    },
    {
      key: 'aktion',
      titel: 'Action',
      wert: (z) => (
        <Button
          size="small"
          // 🔴 Je Zeile unterscheidbar (R16 W1, WCAG 4.1.2): mehrere Disputes tragen
          // sonst denselben Knopfnamen „Schließen".
          aria-label={`Payment disruption ${z.dispute_id} close`}
          onClick={() => { setZeile(z); setMeldung(null); setFehler(null) }}
        >
          Close
        </Button>
      ),
    },
  ]

  return (
    <Gruppe titel="Payment disruptions">
      {meldung && <Alert severity="success" onClose={() => setMeldung(null)}>{meldung}</Alert>}
      {stoerungen.length > 0 ? (
        <DatenTabelle
          name="Payment disruptions"
          spalten={spalten}
          zeilen={stoerungen}
          schmal={schmal}
          schluessel={(z) => z.dispute_id}
        />
      ) : (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No open payment disruptions.
        </Typography>
      )}
      <Erklaerung>
        A chargeback is not a correction document — it is closed by hand, with author.
      </Erklaerung>
      <Dialog
        open={Boolean(zeile)}
        onClose={sendet ? undefined : () => setZeile(null)}
        aria-labelledby="dispute-schliessen-titel"
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle id="dispute-schliessen-titel">Close payment disruption</DialogTitle>
        <DialogContent>
          <Stack spacing={SP.card} sx={{ pt: SP.chip }}>
            {/* S4a: der Bestellname als eigene Zeile ueber dem Satz, mit Rueckfall
                auf die ID — dieselbe Form wie im Refund-Schliessdialog: Nomen davor
                (R16 C1), `subtitle1` unter dem `h6`-Dialogtitel (R16 W3), und der
                Satz darunter wiederholt die Bestellung nicht (R16 W2). */}
            <Typography variant="subtitle1" component="p" data-testid="dispute-bestellname"
              sx={{ wordBreak: 'break-word' }}>
              Order <BestellLink eintrag={zeile} />
            </Typography>
            <Typography variant="body2">
              Dispute {zeile?.dispute_id} is closed as resolved. The author is recorded.
            </Typography>
            {fehler && <Alert severity="error">{fehler}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setZeile(null)} disabled={sendet}>Cancel</Button>
          <Button
            variant="contained"
            onClick={schliesse}
            disabled={sendet}
            startIcon={sendet ? <CircularProgress size={20} color="inherit" aria-hidden="true" /> : null}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Gruppe>
  )
}

function Tagesabschluss({ stand, schmal, tag, setTag, fehler }) {
  // 🔴 Bei JEDEM Aufbau frisch, nie beim ersten eingefroren: wer die Seite über
  //    Mitternacht offen hat, hat danach ein anderes „heute".
  const heute = berlinDateISO(new Date())
  return (
    <Stack spacing={SP.card}>
      <TextField
        size="small"
        type="date"
        label="Day"
        InputLabelProps={{ shrink: true }}
        // Ein künftiger Tag hätte nur Nullen („am <morgen>: 0") — das Feld endet bei heute.
        inputProps={{ max: heute }}
        value={tag}
        onChange={(e) => setTag(e.target.value)}
        error={Boolean(fehler)}
        helperText={fehler || 'Berlin calendar day'}
        sx={{ alignSelf: 'flex-start' }}
      />
      <Zustand
        stand={stand}
        kennung="tagesabschluss"
        fehlerTitel="The daily close could not be loaded."
        eingabeFehler={fehler}
      >
        {(daten, neuLaden) => {
          const forge = daten?.forge || {}
          const modus = forge.modus || {}
          const refund = daten?.refund || {}
          const zustand = refund.zustand || {}
          const dispute = daten?.dispute || {}
          const stoerungen = dispute.items || []
          const bb = daten?.bb || {}
          const shopify = daten?.shopify || {}
          const laeufe = daten?.laeufe || []
          const nichtInForge = daten?.nicht_in_forge || []
          // Gegen den Tag der ANTWORT, nicht der Eingabe — beim Neuladen gehört die
          // Anzeige noch zum alten Tag. `!==`, nicht `<`: auch ein künftiger Tag ist
          // nicht der Tag, an dem BB und Läufe gemessen wurden.
          const nichtHeute = typeof daten?.tag === 'string' && daten.tag !== heute
          const amTag = `on ${tagText(daten?.tag)}`
          const BESTAND = 'Stock now'
          // Die BB-Kacheln sagen ihren Zeitbezug wie alle anderen — aus der ANTWORT
          // (`bb.stand`), nicht angenommen: fehlt `stand`, steht nur die Lage da.
          const bbHinweis = (lage) => (bb.stand === 'jetzt' ? `${BESTAND} · ${lage}` : lage)
          return (
            <Stack spacing={SP.md}>
              {/* 🔴 **EIN Momentaufnahme-Hinweis je Abschnitt** (S8, §3.2 Nr. 56).
                  Bis hierher stand er dreimal (Sweeper, BB, Läufe) — dreimal
                  dieselbe Aussage über denselben Abschnitt. Die Bedingung ist
                  dieselbe wie zuvor, nur zusammengefasst: irgendein Teil dieser
                  Antwort ist eine Momentaufnahme (`stand: "jetzt"`), und der Tag
                  ist nicht heute. */}
              {nichtHeute
                && (bb.stand === 'jetzt'
                  || shopify.stand === 'jetzt'
                  || laeufe.some((l) => l.stand === 'jetzt'))
                && <Momentaufnahme tag={daten.tag} />}

              {/* 🔴 **Zahlungsstörungen am Gruppenanfang** (S8, §3.2 Nr. 57): was
                  klemmt, steht vor dem, was läuft. Der benannte Leerzustand bleibt
                  — „nur bei Befall" gilt für den Inhalt, nicht für die Gruppe. */}
              <Zahlungsstoerungen stoerungen={stoerungen} schmal={schmal} neuLaden={neuLaden} />

              <Gruppe titel="Forge">
                <Kacheln kacheln={[
                  { label: 'Created', value: forge.erzeugt, hint: amTag },
                  { label: 'Corrected', value: forge.korrigiert, hint: amTag },
                  { label: 'Mailed', value: forge.versandt, hint: amTag },
                  { label: 'Rejected', value: forge.abgewiesen, hint: amTag },
                  // 🔴 S1 (§2.3, §6 Nr. 7): DREI Stellen führen eine Zahl „Open
                  //    blocks", zwei davon auf Operations. Die Kachel zählt
                  //    UNGEFILTERT (Testbelege mit), die Band-Zeile GEFILTERT.
                  //    Zwei Bedeutungen, zwei Namen.
                  { label: 'Open blocks, all invoices', value: forge.offene_sperren, hint: BESTAND },
                  { label: 'Without number', value: forge.ohne_nummer, hint: BESTAND },
                  {
                    label: 'Issuance',
                    value: modusText(modus.issue) ?? undefined,
                    hint: modus.correction == null ? undefined : `Correction: ${modusText(modus.correction)}`,
                  },
                ]}
                />
                <Erklaerung>
                  {/* 🔴 S1-Akz. 8: nur der ERSTE Satz ist neu. Die Kachel zählt
                      Testbelege mit, die Liste nicht — deshalb können die Zahlen
                      auseinanderfallen, und das ist keine Abweichung. */}
                  These tiles count test invoices too, so they can differ from the invoice list.
                  “on …” counts the events of the
                  Berlin day; “Stock now” is the current state, regardless of the selected day.
                </Erklaerung>
              </Gruppe>

              <Gruppe titel="Refund decisions">
                <Kacheln kacheln={[
                  { label: 'New', value: refund.neu_heute, hint: amTag },
                  { label: 'Open', value: zustand.offen, hint: BESTAND },
                  { label: 'Pending', value: zustand.ausstehend, hint: BESTAND },
                  { label: 'Resolved', value: zustand.erledigt, hint: BESTAND },
                  { label: 'Superseded', value: zustand.ueberholt, hint: BESTAND },
                ]}
                />
              </Gruppe>

              <Gruppe titel="Receipt chain (BB)">
                <Kacheln kacheln={[
                  { label: 'Receipt chain stuck', value: bb.haengt?.anzahl, hint: bbHinweis('Situation f') },
                  { label: 'Incomplete in BB', value: bb.unvollstaendig?.anzahl, hint: bbHinweis('Situation e') },
                ]}
                />
                {(bb.verteilung || []).length > 0 ? (
                  <DatenTabelle
                    name="BB states"
                    spalten={VERTEILUNG_SPALTEN}
                    zeilen={bb.verteilung}
                    schmal={schmal}
                    schluessel={(z) => `${z.art}-${z.bb_state}`}
                  />
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No invoice in Forge yet — no BB states.
                  </Typography>
                )}
                <Erklaerung>BB state: last written by the receipt run.</Erklaerung>
                <LetzterAbgleich ab={bb.abgleich} />
              </Gruppe>

              <LetzterSweeper sh={shopify} />

              <Gruppe titel="Runs">
                {laeufe.length > 0 ? (
                  <DatenTabelle
                    name="Runs"
                    spalten={LAUF_SPALTEN}
                    zeilen={laeufe}
                    schmal={schmal}
                    schluessel={(z) => z.kind}
                  />
                ) : (
                  // 🔴 S8: der Leerzustand nennt seinen Grund (`unknown`). „Keine
                  //    Läufe" ist hier keine Messung — die Antwort hat keine
                  //    genannt, und ob welche laufen, weiß die Seite nicht.
                  <EmptyState
                    reason="unknown"
                    message="The response names no run."
                  />
                )}
              </Gruppe>

              {nichtInForge.length > 0 && (
                // 🔴 Ausdrücklich als LISTE, ohne Zahl: eine 0 sähe aus wie
                //    „geprüft, nichts gefunden" (Regel 17 §3b).
                <Alert severity="info">
                  <AlertTitle>Not tracked in Forge</AlertTitle>
                  Forge cannot supply these figures — deliberately no number here.
                  <Box component="ul" aria-label="Not tracked in Forge" sx={{ mt: SP.chip, mb: 0, pl: SP.md }}>
                    {nichtInForge.map((t) => <li key={t}>{t}</li>)}
                  </Box>
                </Alert>
              )}
            </Stack>
          )
        }}
      </Zustand>
    </Stack>
  )
}

// ── (d) Gelangensnachweise (IgL) ────────────────────────────────────────────
// S6.1f (#6): GET /api/invoicing/admin/igl-nachweise. Je offene IgL-Rechnung die
// drei Nachweise (P6-O2 (b)); Warnung ab Tag 30 ohne Zustellnachweis. Die Route
// trägt außerdem die Störungslage `b2b_lage_unlesbar` aus #4 (P6-W21).

export const IGL_ART_LABEL = {
  versandauftrag: 'Shipping order',
  tracking_zugestellt: 'Delivery',
  bzst_bestaetigung: 'BZSt confirmation',
}

/**
 * Die Störungslagen der IgL-Fläche — deutsche Überschrift je Code.
 *
 * `betrieb_routes.py` liefert im Feld `stoerung.lage` den **Code** (heute genau
 * `b2b_lage_unlesbar`, `betrieb_routes.py`). Ein roher Code ist keine
 * Überschrift (R16 B1, dieselbe Klasse wie das N7-Gate B2): hier steht der
 * deutsche Satz, der Code höchstens als `<code>` darunter. Ein unbekannter Code
 * fällt auf einen neutralen deutschen Satz — nie auf den Code selbst.
 */
export const IGL_STOERUNG_LABEL = {
  b2b_lage_unlesbar: 'B2B customer read failed',
}
export const IGL_STOERUNG_UNBEKANNT = 'Unknown disruption'

const IGL_SPALTEN = [
  {
    key: 'nummer',
    titel: 'Invoice',
    wert: (z) => (
      <>
        {z.nummer
          ? <Link component={RouterLink} to={`/invoicing/${z.invoice_id}`}>{z.nummer}</Link>
          : <RechnungsLink id={z.invoice_id} />}
        {/* Ohne auswertbare Bestellreferenz ist der Zustellnachweis nicht
            zuordenbar (R15 W5) — deutscher Hinweis, kein roher Code. */}
        {z.referenz_fehlt && <Gedaempft> · without order reference</Gedaempft>}
      </>
    ),
  },
  { key: 'versandtag', titel: 'Shipping day', wert: (z) => (z.versandtag ? tagText(z.versandtag) : STRICH) },
  { key: 'tage', titel: 'Days since shipping', rechts: true, wert: (z) => ganzzahl(z.tage_seit_versand) },
  {
    key: 'fehlt',
    titel: 'Missing proofs',
    wert: (z) => ((z.fehlende_arten || []).length
      ? z.fehlende_arten.map((a) => IGL_ART_LABEL[a] || a).join(', ')
      : <Gedaempft>complete</Gedaempft>),
  },
  {
    key: 'land',
    titel: 'Country',
    // P6-W11/P6-O10 (2): die Zustellzeile trägt das Land der Lieferanschrift,
    // kein Ereignisland — bis zur Antwort heisst es „vorläufig". Das Land selbst
    // kommt als deutscher Name (`land_name`, R16 B2); ohne Zustellzeile gibt es
    // keins. `land` (ISO-Code) ist der Rückfall, falls kein Name aufgelöst wurde.
    // „vorläufig" hängt NICHT am Land: auch ohne aufgelösten Namen bleibt die
    // Kennzeichnung sichtbar (wie vor R16 B2).
    wert: (z) => {
      const name = z.land_name || z.land
      if (!name && !z.vorlaeufig) return <Gedaempft>—</Gedaempft>
      return (
        <>
          {name || <Gedaempft>—</Gedaempft>}
          {z.vorlaeufig && <> <BetriebPill tone="warn">provisional</BetriebPill></>}
        </>
      )
    },
  },
  {
    key: 'warnung',
    titel: 'Warning',
    wert: (z) => (z.warnung
      ? <BetriebPill tone="warn">Delivery missing</BetriebPill>
      : <Gedaempft>—</Gedaempft>),
  },
]

function IglNachweise({ stand, schmal }) {
  return (
    <Zustand stand={stand} kennung="igl-nachweise"
             fehlerTitel="The proofs of arrival could not be loaded.">
      {(daten) => {
        const nachweise = daten?.nachweise || []
        const stoerung = daten?.stoerung
        return (
          <Stack spacing={SP.tight}>
            {stoerung && stoerung.anzahl > 0 && (
              <Alert severity="warning">
                <AlertTitle>
                  Disruption: {IGL_STOERUNG_LABEL[stoerung.lage] || IGL_STOERUNG_UNBEKANNT}
                </AlertTitle>
                {stoerung.anzahl} open orders: invoice issuance is blocked until the
                customer read succeeds again.
                {' '}
                <Box component="code" sx={{ fontFamily: 'monospace', fontSize: FS.sm }}>
                  {stoerung.lage}
                </Box>
              </Alert>
            )}
            {nachweise.length === 0 ? (
              <Alert severity="info">
                <AlertTitle>No open intra-Community invoice.</AlertTitle>
                A proof is created with the first issued intra-Community supply.
              </Alert>
            ) : (
              <DatenTabelle
                name="Proofs of arrival"
                spalten={IGL_SPALTEN}
                zeilen={nachweise}
                schmal={schmal}
                schluessel={(z) => z.invoice_id}
              />
            )}
            <Erklaerung>
              Per intra-Community invoice three proofs: shipping order, delivery and BZSt confirmation. The
              warning applies from 30 days without a delivery proof. “Provisional” means: the country comes from
              the delivery address, not from a delivery event. “Without order reference” means: the
              invoice cannot be assigned to an order — the delivery proof is then not
              determinable.
            </Erklaerung>
          </Stack>
        )
      }}
    </Zustand>
  )
}

// ── (e) Ausfuhrnachweise (S8, BA11) ─────────────────────────────────────────
// GET /api/invoicing/admin/ausfuhr-nachweise. Die vierte Untergruppe von
// „Month close" (§2.3). 🔴 **Keine Band-Zeile:** die Fläche filtert nicht, eine
// Zeile im Band hinge an Testbelegen (a2, 01.11.). Die Zahl steht im Kopf des
// zugeklappten Abschnitts (§1.6).
//
// 🔴 **Die Frist wird NICHT bewertet.** Es gibt keinen Fristwert im Code
// (Verweis: RA-030, Ampel rot) — die Seite zeigt das Alter in Tagen und sonst
// nichts. Kein Warnton, keine Ampel, kein „bis sie weg sind".

/** `art` der offenen Vorgänge — die vier aus `betrieb_routes.OFFENE_ARTEN`. */
export const AUSFUHR_ART_LABEL = {
  erwartet: 'Expected',
  eingegangen: 'Received',
  abgelehnt: 'Rejected',
  abgelaufen: 'Expired',
}

const AUSFUHR_SPALTEN = [
  {
    key: 'nummer',
    titel: 'Invoice',
    wert: (z) => (z.nummer
      ? <Link component={RouterLink} to={`/invoicing/${z.invoice_id}`}>{z.nummer}</Link>
      : <RechnungsLink id={z.invoice_id} />),
  },
  { key: 'bestellung', titel: 'Order', wert: (z) => bestellzeile(z) },
  {
    key: 'art',
    titel: 'State',
    wert: (z) => (AUSFUHR_ART_LABEL[z.art] || z.art),
  },
  {
    key: 'alter',
    titel: 'Days',
    rechts: true,
    // 🔴 Ohne Alter steht ein benannter Satz, keine 0 (Regel 17 §3b): die Zeile
    //    ist die Aufnahme in die Fläche, das Alter kommt aus ihr.
    wert: (z) => (z.alter_unbekannt || z.seit_tagen == null
      ? <Gedaempft>unknown</Gedaempft>
      : ganzzahl(z.seit_tagen)),
  },
  {
    key: 'nachweis',
    titel: 'Proof',
    wert: (z) => (z.nachweis_vorhanden ? 'present' : <Gedaempft>missing</Gedaempft>),
  },
  {
    key: 'status',
    titel: 'Invoice status',
    wert: (z) => (STATUS_LABEL[z.status] || z.status || STRICH),
  },
  {
    key: 'ust',
    titel: 'Tax',
    rechts: true,
    // Geldwert als String oder `null` — nie 0 (Hausform dieser Seite).
    wert: (z) => (z.ust != null ? betrag(Number(z.ust)) : <Gedaempft>unknown</Gedaempft>),
  },
]

/**
 * Der Kopf-Hinweis von „Month close": „Export proofs: n open".
 *
 * 🔴 **Eine Zahl ohne Bewertung.** Es gibt keinen Fristwert im Code
 *    (Verweis: RA-030, Ampel rot) — der Hinweis zählt offene Vorgänge und sagt
 *    nichts darüber, ob sie zu spät sind. a2, 01.11., wörtlich: „Die zwei
 *    Testvorgänge gehen nicht von selbst weg, und löschen tun wir auf Prod
 *    nichts. Plane also damit, dass ‚2 open' stehen bleibt."
 *
 * 🔴 **Scheitert die Quelle, steht „unknown", nie nichts** (Regel 22 Nr. 3/4).
 *    Solange noch KEINE Antwort da ist, gibt es keinen Hinweis — „Unknown" wäre
 *    für einen Moment behauptet, in dem nur geladen wird. Beim Nachladen bleibt
 *    der zuletzt gelesene Wert stehen (R16 W3).
 *
 * 🔴 **Ein leerer Monitor bekommt keinen Zahlentext:** die Route meldet ihn als
 *    `leer.grund = "unknown"`; eine „0" wäre die Behauptung, es sei nichts offen.
 *
 * @returns {string|null} der Hinweis oder `null` (dann bleibt der Titel nackt)
 */
export function ausfuhrKopfHinweis(stand) {
  // 🔴 R16 W3 / R15 W1: `null` nur, solange es noch KEINE Antwort gibt. Beim
  //    Nachladen (`⟳`) behält `useLesen` die alte Antwort und setzt nur
  //    `laedt: true` (`:257`) — ein blosses `if (stand.laedt)` liesse den Kopf
  //    bei jedem Refresh kurz nackt, obwohl die Zahl bekannt ist.
  if (stand.laedt && stand.daten === null) return null
  if (stand.fehler || stand.daten === null) return 'Export proofs: unknown'
  const anzahl = stand.daten?.anzahl
  if (!Number.isFinite(anzahl) || anzahl <= 0) return null
  return `Export proofs: ${anzahl} open`
}

function Ausfuhrnachweise({ stand, schmal }) {
  return (
    <Zustand stand={stand} kennung="ausfuhr-nachweise"
             fehlerTitel="The export proofs could not be loaded.">
      {(daten) => {
        const vorgaenge = daten?.vorgaenge || []
        const ohneAlter = daten?.ohne_alter || 0
        return (
          <Stack spacing={SP.tight}>
            {vorgaenge.length === 0 ? (
              // 🔴 Ein leerer Monitor heißt laut Route `leer.grund = "unknown"` —
              //    er behauptet NICHT „nichts zu tun" (Regel 22 Nr. 3/4).
              <EmptyState
                reason={daten?.leer?.grund || 'unknown'}
                message="No open export procedure."
                // 🔴 Englisch wie die Seite — der `leer.text` der Route ist
                //    deutsch und bleibt in der Antwort, nicht auf dem Schirm.
                detail="The area cannot rule out candidates without an event row."
              />
            ) : (
              <>
                <DatenTabelle
                  name="Export proofs"
                  spalten={AUSFUHR_SPALTEN}
                  zeilen={vorgaenge}
                  schmal={schmal}
                  schluessel={(z) => z.invoice_id}
                />
                {ohneAlter > 0 && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {ohneAlter} {ohneAlter === 1 ? 'procedure has' : 'procedures have'} no age yet.
                  </Typography>
                )}
              </>
            )}
            <Erklaerung>
              Open self-export procedures, oldest first. The age in days is shown; the
              deadline itself is not evaluated — there is no deadline value in the code.
            </Erklaerung>
          </Stack>
        )
      }}
    </Zustand>
  )
}

// ── Der Reiter ─────────────────────────────────────────────────────────────

/**
 * Kopf, Reiterleiste und Paneel — OHNE eigenen `PageContainer`: den hält die
 * Weiche in `InvoiceList.jsx`, damit ein Reiterwechsel die Seite nicht neu
 * aufbaut (keine Einblendung bei jedem Wechsel).
 *
 * @param reiterLeiste  die Reiterleiste der Seite (aus `InvoiceList.jsx`)
 * @param panel         `role`/`id`/`aria-labelledby` des Reiterpaneels
 */
// ── N3-B (S5.8) — die Handrechnung zu einer vorhandenen Bestellung ─────────
// Nur **Weg B** (zu einer Bestellung); Weg A (ohne Bestellung) kommt mit PR A.
const HAND_PFAD = '/api/invoicing/admin/handrechnung/zu-bestellung'

/** Die Platzhalter-Nummer der Vorschau (Backend: `issuance.PLATZHALTER_NUMMER`). */
const ENTWURF_NUMMER = 'ENTWURF-000000'

/** Die zwei Steuerfälle, die die Handrechnung ausstellen darf (§ 2.3 Schritt 7). */
const HAND_STEUERFAELLE = ['domestic', 'eu_b2c_oss']

/** Der Grund-Katalog der drei Restfälle (P6-O8). */
const HAND_GRUENDE = [
  { wert: 'p6_restfall:invalid', text: 'VAT ID invalid or unconfirmed',
    begriff: 'USt-IdNr. ungültig oder unbestätigt' },
  { wert: 'p6_restfall:e', text: 'Stock tax — case (e)',
    begriff: 'Bestandsteuer — Fall (e)' },
  { wert: 'p6_restfall:k2', text: 'K2 — payment without receipt',
    begriff: 'K2 — Zahlung ohne Beleg' },
]

/**
 * Die Kurzcodes der zwei Routen in Klartext — die Route liefert den Code, die
 * Fläche den Satz.
 *
 * 🔴 **Vollständig, und das ist geprüft:** `__tests__/Betrieb.test.jsx` liest die
 * Codes aus `handrechnung.py` UND `handrechnung_routes.py` und macht die Fläche
 * rot, sobald dort ein Code dazukommt, der hier fehlt (R16 B2). Ein Code ohne
 * Satz zeigte dem Bediener vorher den nackten Schlüssel (`betrag:negativ`).
 */
export const HAND_FEHLER_TEXT = {
  // Konto, Riegel, Zustand
  kein_schreibkonto: 'This account may not issue an invoice.',
  handrechnung_gesperrt: 'The manual invoice is blocked — INVOICING_MANUAL_MODE is not set to “live”.',
  bestellung_fehlt: 'Please give an order number.',
  kein_offener_intent: 'There is no open invoice order for this order number.',
  schon_ausgestellt: 'This invoice is already issued.',
  kein_sperrereignis: 'This order is not blocked — it belongs to the automatic path, not the manual one.',
  shopify_unlesbar: 'The order is not readable from Shopify.',
  lieferdatum_fehlt: 'The order carries no shipment — there is no delivery date.',
  lieferdatum_in_zukunft: 'The delivery date is in the future.',
  lieferdatum_nicht_eindeutig: 'The delivery date of this order is not certain — a shipment is not completed, or the shipments fall on different days.',
  // Eingabe
  grund_fehlt: 'Please pick a reason.',
  grund_unbekannt: 'This reason is unknown.',
  // N3-A (ohne Bestellung)
  kundentyp_unbekannt: 'This customer type is unknown.',
  lieferdatum_unlesbar: 'The delivery date is unreadable.',
  lieferdatum_nicht_heute: 'Without an order the invoice is created on the delivery day — today. Another date is not permitted.',
  idempotenzschluessel_fehlt: 'The operation is not prepared — please “Load” or “Preview” first.',
  idempotenzschluessel_unbekannt: 'The operation key is unknown — please reload.',
  'empfaenger:unlesbar': 'The recipient is missing.',
  'empfaenger:unvollstaendig': 'The recipient needs a name and a complete address (§ 14 Abs. 4 Nr. 1 UStG).',
  steuerfall_unzulaessig: 'This tax case is not accessible to the manual invoice (intra-Community supply handles #7).',
  'steuerfall:unzulaessig': 'This tax case is not accessible to the manual invoice (intra-Community supply handles #7).',
  'steuerfall:widerspruch': 'The tax case does not match the delivery country of the order.',
  'lieferland:fehlt': 'Without a delivery country no rate can be determined.',
  'lieferland:unbekannt': 'The delivery country is not a known country code (ISO 3166, e.g. DE, AT, CH).',
  'positionen:leer': 'The invoice needs items.',
  'position:unlesbar': 'An item is unreadable.',
  'menge:ungueltig': 'A quantity is invalid — it must be greater than 0.',
  'betrag:negativ': 'A gross unit price is negative — that is not permitted.',
  'betrag:unlesbar': 'A gross unit price is not a number.',
  'zahl:nicht_endlich': 'A number is infinite or not a number — that is not permitted.',
  'zahl:zu_gross': 'A number is above the permitted limit.',
  'ausfuhr:nachweis_unbestaetigt': 'The export needs the confirmed proof of export (§ 6 Abs. 4 UStG).',
  'satz:ausserhalb_der_liste': 'A rate is outside the permitted list.',
  'satz:keine_quelle': 'There is no stored rate for this delivery country.',
  'warengruppe:keine_quelle': 'There is no stored rate list for this product group.',
  // Steuerprüfung und Ausstellung
  plausibilitaet: 'The tax check halts.',
  vorflug_gescheitert: 'The preview could not render a document.',
  ausstellung_gescheitert: 'Issuance failed — nothing was written.',
  gesperrt: 'The invoice is blocked — it is not issued by hand either.',
  nicht_ausstellbar: 'The invoice cannot be issued in this state.',
}

/** Der Satz für einen Code, den die Fläche nicht kennt — der Code steht klein daneben. */
const HAND_FEHLER_ALLGEMEIN = 'The manual invoice rejected the input.'

/**
 * Der Abschnitt „Manuelle Rechnung" (Weg B): Bestellnummer → laden → Positionen
 * setzen → Vorschau (schreibt nichts) → ausstellen.
 *
 * Die Vorschau rendert `ENTWURF-000000` und schreibt **nichts**; erst „Ausstellen"
 * vergibt eine Nummer. `mail` steuert den automatischen Versand — ein späterer
 * Admin-Neuversand ist davon unberührt (D8).
 */
function ManuelleRechnung({ onSchmutzig }) {
  const [bestellung, setBestellung] = useState('')
  const [steuerfall, setSteuerfall] = useState('domestic')
  const [grund, setGrund] = useState('p6_restfall:e')
  const [mail, setMail] = useState(true)
  const [zeilen, setZeilen] = useState([])
  const [saetze, setSaetze] = useState({})
  const [hinweise, setHinweise] = useState([])
  const [warnungen, setWarnungen] = useState([])
  const [summen, setSummen] = useState(null)
  const [lieferdatum, setLieferdatum] = useState(null)
  const [ausstellungsdatum, setAusstellungsdatum] = useState(null)
  const [laedt, setLaedt] = useState(false)
  // Erstladen = Skeleton, Nachladen = LinearProgress (Seitenmuster, `Zustand`).
  // `ersteLadung` bleibt true, bis die ERSTE Anfrage durch ist.
  const [ersteLadung, setErsteLadung] = useState(true)
  const [fehler, setFehler] = useState(null)
  const [erfolg, setErfolg] = useState(null)
  // Der Bestätigungsdialog vor der unwiderruflichen Ausstellung (R16 B1).
  const [dialogOffen, setDialogOffen] = useState(false)
  const [sendet, setSendet] = useState(false)

  const gesperrt = Boolean(erfolg)

  const setZeile = (index, feld, wert) => setZeilen((alt) => alt.map(
    (z, i) => (i === index ? { ...z, [feld]: wert } : z)))

  const alsFehler = (e) => {
    const daten = e?.response?.data || {}
    const code = daten.error
    // Bekannter Code → deutscher Satz; unbekannter → allgemeiner Satz, der Code
    // klein daneben; gar keine Antwort (Netz) → die Meldung selbst.
    const text = code
      ? (HAND_FEHLER_TEXT[code] || HAND_FEHLER_ALLGEMEIN)
      : (e?.message || 'Unknown error')
    return {
      text,
      code: code && !HAND_FEHLER_TEXT[code] ? code : null,
      // `text` (Singular) traegt den deutschen Satz der Route, `gruende`/`texte`
      // die Steuerbefunde — alle drei gehören an die Fläche (R16 B2).
      gruende: [
        ...(daten.text ? [daten.text] : []),
        ...(daten.gruende || []),
        ...(daten.texte || []),
      ],
    }
  }

  const positionen = () => zeilen.map((z) => ({
    beschreibung: z.beschreibung,
    warengruppe: z.warengruppe,
    menge: z.menge,
    einzelbrutto: z.einzelbrutto,
    satz: z.satz,
    shopify_line_item_id: z.shopify_line_item_id,
  }))

  const rumpf = (mitPositionen) => ({
    bestellung,
    steuerfall,
    grund,
    mail,
    ...(mitPositionen ? { positionen: positionen() } : {}),
  })

  const ruf = async (pfad, mitPositionen) => {
    setLaedt(true); setFehler(null); setErfolg(null)
    try {
      const { data } = await api.post(`${HAND_PFAD}/${pfad}`, rumpf(mitPositionen))
      return data
    } catch (e) {
      setFehler(alsFehler(e))
      return null
    } finally {
      setLaedt(false); setErsteLadung(false)
    }
  }

  const laden = async () => {
    setZeilen([]); setSaetze({}); setHinweise([]); setWarnungen([]); setSummen(null)
    setLieferdatum(null); setAusstellungsdatum(null)
    const data = await ruf('vorschau', false)
    if (!data) return
    setZeilen((data.positionen_vorschlag || []).map((p) => ({ ...p, satz: '' })))
    setSaetze(data.saetze || {})
    setHinweise(data.hinweise || [])
    setWarnungen(data.warnungen || [])
    setLieferdatum(data.lieferdatum || null)
    setAusstellungsdatum(data.ausstellungsdatum || null)
  }

  const vorschau = async () => {
    setSummen(null)
    const data = await ruf('vorschau', true)
    if (!data) return
    setHinweise(data.hinweise || [])
    setWarnungen(data.warnungen || [])
    setSummen(data.ergebnis ? data.ergebnis.summen : null)
  }

  /** Der Dialog bestätigt; erst der zweite Klick stellt wirklich aus. */
  const ausstellen = async () => {
    setSendet(true)
    const data = await ruf('ausstellen', true)
    setSendet(false)
    if (data) { setErfolg(data); setDialogOffen(false) }
  }

  /** Nach dem Erfolg: alles zurück auf Anfang — eine neue Handrechnung. */
  const weitereHandrechnung = () => {
    setBestellung(''); setSteuerfall('domestic'); setGrund('p6_restfall:e'); setMail(true)
    setZeilen([]); setSaetze({}); setHinweise([]); setWarnungen([]); setSummen(null)
    setLieferdatum(null); setAusstellungsdatum(null)
    setFehler(null); setErfolg(null); setErsteLadung(true)
  }

  const bereit = !gesperrt && bestellung.trim() !== '' && zeilen.length > 0
    && zeilen.every((z) => String(z.satz).trim() !== '' && String(z.einzelbrutto).trim() !== '')

  // 🔴 R16 W2: meldet ungesicherte Eingaben an den Weg-Schalter — ein Wechsel hängt
  // diese Fläche ab und verwirft sie sonst ohne Warnung.
  const schmutzig = bestellung.trim() !== '' || zeilen.length > 0 || Boolean(erfolg)
  useEffect(() => { onSchmutzig?.(schmutzig) }, [schmutzig, onSchmutzig])

  /** Die Fehlerfläche: im Dialog, solange er offen ist — sonst im Abschnitt. */
  const fehlerFlaeche = (
    <Alert severity="error">
      <AlertTitle>{fehler?.text}</AlertTitle>
      {fehler?.code && <Gedaempft>{`Code: ${fehler.code}`}</Gedaempft>}
      {fehler?.gruende.length > 0 && (
        <Box component="ul" sx={{ m: 0, pl: SP.md }}>
          {fehler.gruende.map((g) => <li key={g}>{g}</li>)}
        </Box>
      )}
    </Alert>
  )

  return (
    <Stack spacing={SP.md}>
      <Erklaerung>
        Issues an invoice for an existing order by hand — for the blocked residual
        cases that the automatic path does not issue. The preview writes nothing;
        only “Issue” assigns a number.
      </Erklaerung>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={SP.chip} alignItems="flex-start">
        <TextField
          size="small"
          label="Order number"
          value={bestellung}
          onChange={(e) => setBestellung(e.target.value)}
          placeholder="SHOP-13820"
          inputProps={{ 'aria-label': 'Order number' }}
          disabled={gesperrt}
        />
        <TextField
          size="small"
          select
          label="Tax case"
          value={steuerfall}
          onChange={(e) => setSteuerfall(e.target.value)}
          inputProps={{ 'aria-label': 'Tax case' }}
          disabled={gesperrt}
        >
          {HAND_STEUERFAELLE.map((wert) => (
            <MenuItem key={wert} value={wert}>{STEUERFALL_LABEL[wert] || wert}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          select
          label="Reason"
          value={grund}
          onChange={(e) => setGrund(e.target.value)}
          inputProps={{ 'aria-label': 'Reason' }}
          disabled={gesperrt}
        >
          {/* 🔴 Der Rückverweis als natives `title`, NICHT als `Tooltip`-Wrapper:
              MUI `Select` sucht das gewaehlte Kind unter seinen DIREKTEN Kindern
              (`props.value`) — ein Wrapper hat kein `value`, und das Select
              zeigte dann nichts an. Der Eintrag ist fokussierbar, der Begriff
              steht als Beschreibung daran (R16 H2). */}
          {HAND_GRUENDE.map((g) => (
            <MenuItem key={g.wert} value={g.wert} title={g.begriff}>{g.text}</MenuItem>
          ))}
        </TextField>
        <DisabledReason
          reason={laedt
            ? null
            : gesperrt ? 'This manual invoice has already been issued.'
              : bestellung.trim() === '' ? 'Enter an order number first.' : null}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={laden}
            disabled={laedt || gesperrt || bestellung.trim() === ''}
          >
            Load
          </Button>
        </DisabledReason>
      </Stack>

      {fehler && !dialogOffen && fehlerFlaeche}

      {laedt && ersteLadung && (
        <Paper elevation={0} sx={PANEL_SX} data-testid="handrechnung-laden" aria-busy="true">
          <Stack spacing={SP.tight}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} data-testid="handrechnung-skelett" variant="rounded" height={32} />
            ))}
          </Stack>
        </Paper>
      )}
      {laedt && !ersteLadung && <LinearProgress aria-label="Updating" />}

      {(lieferdatum || ausstellungsdatum) && (
        <Gruppe titel="Dates">
          <Erklaerung>
            Delivery date from the shipment (service date), issuance date is today
            (invoice date) — the invoice carries both.
          </Erklaerung>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={SP.card}>
            <Box>
              <Gedaempft>Delivery date</Gedaempft>
              <Typography component="div">{formatBerlinDate(lieferdatum, { dateOnly: true })}</Typography>
            </Box>
            <Box>
              <Gedaempft>Issuance date</Gedaempft>
              <Typography component="div">{formatBerlinDate(ausstellungsdatum, { dateOnly: true })}</Typography>
            </Box>
          </Stack>
        </Gruppe>
      )}

      {zeilen.length > 0 && (
        <Gruppe titel="Items">
          <Erklaerung>The operator sets the rate and gross unit price — the rate comes from the tax case's list.</Erklaerung>
          <Paper elevation={0} sx={{ ...PANEL_SX, overflowX: 'auto' }}>
            <Table size="small" aria-label="Items">
              <TableHead>
                <TableRow>
                  <TableCell>Description</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Gross unit</TableCell>
                  <TableCell>Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {zeilen.map((z, i) => (
                  <TableRow key={`${z.shopify_line_item_id || z.beschreibung}-${i}`}>
                    <TableCell>{z.beschreibung}</TableCell>
                    <TableCell>{z.menge}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={z.einzelbrutto}
                        onChange={(e) => setZeile(i, 'einzelbrutto', e.target.value)}
                        inputProps={{ 'aria-label': `Gross unit ${z.beschreibung}` }}
                        disabled={gesperrt}
                      />
                    </TableCell>
                    <TableCell>
                      {/* 🔴 `native` (R16 B1): bei einem MUI-`Select` (nicht nativ)
                          landet `inputProps` auf dem versteckten Eingabefeld
                          (`aria-hidden`) — das Feld hatte dadurch KEINEN
                          zugänglichen Namen, weder `getByLabelText` noch
                          `getByLabel` fanden es. Ein echtes `<select>` trägt
                          `aria-label` selbst (MUI: „when `native` is `true`, the
                          attributes are applied on the `select` element"), und
                          die leere erste Option hält den Zustand „noch kein Satz"
                          sichtbar statt den ersten Satz vorzutäuschen. */}
                      <TextField
                        size="small"
                        select
                        value={z.satz}
                        onChange={(e) => setZeile(i, 'satz', e.target.value)}
                        SelectProps={{ native: true }}
                        inputProps={{ 'aria-label': `Rate ${z.beschreibung}` }}
                        disabled={gesperrt}
                      >
                        <option value="">Choose rate</option>
                        {(saetze[z.warengruppe] || []).map((s) => (
                          <option key={s} value={s}>{`${s} %`}</option>
                        ))}
                      </TextField>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Gruppe>
      )}

      {(hinweise.length > 0 || warnungen.length > 0) && (
        <Gruppe titel="Notices">
          {hinweise.map((h) => (
            <Alert key={h} severity="info" icon={false}>{h}</Alert>
          ))}
          {warnungen.map((w) => (
            <Alert key={w} severity="warning">{w}</Alert>
          ))}
        </Gruppe>
      )}

      {summen && (
        <Gruppe titel="Preview (writes nothing)">
          <Gedaempft>
            {`Draft ${ENTWURF_NUMMER} · net ${summen.netto} · tax ${summen.steuer} · gross ${summen.brutto}`}
          </Gedaempft>
        </Gruppe>
      )}

      {erfolg && (
        <Alert severity="success">
          <AlertTitle>{`Invoice ${erfolg.invoice_number} issued`}</AlertTitle>
          <RechnungsLink id={erfolg.invoice_id} />
          <MailErgebnis erfolg={erfolg} />
        </Alert>
      )}

      <Stack direction="row" spacing={SP.chip} alignItems="center" flexWrap="wrap">
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={mail}
              onChange={(e) => setMail(e.target.checked)}
              disabled={gesperrt}
            />
          )}
          label="Send invoice to the customer"
        />
        <DisabledReason
          reason={laedt
            ? null
            : gesperrt ? 'This manual invoice has already been issued.'
              : bestellung.trim() === '' ? 'Enter an order number first.'
                : zeilen.length === 0 ? 'Add at least one line first.'
                  : !bereit ? 'Complete every line first.' : null}
        >
          <Button variant="outlined" size="small" onClick={vorschau} disabled={laedt || !bereit}>
            Preview
          </Button>
        </DisabledReason>
        <DisabledReason
          reason={laedt
            ? null
            : gesperrt ? 'This manual invoice has already been issued.'
              : bestellung.trim() === '' ? 'Enter an order number first.'
                : zeilen.length === 0 ? 'Add at least one line first.'
                  : !bereit ? 'Complete every line first.' : null}
        >
          <Button variant="contained" size="small" onClick={() => setDialogOffen(true)} disabled={laedt || !bereit}>
            Issue
          </Button>
        </DisabledReason>
        {gesperrt && (
          <Button variant="outlined" size="small" onClick={weitereHandrechnung}>
            Another manual invoice
          </Button>
        )}
      </Stack>

      {/* 🔴 R16 B1: die Ausstellung ist unwiderruflich — sie verbraucht eine
          Rechnungsnummer und sendet per Vorgabe eine Kundenmail. Muster:
          „Rechnung erneut senden" (`InvoiceDetail.jsx`) und „Zahlungsstörung
          schließen" (`Betrieb.jsx`). Der Fehler steht IM Dialog. */}
      <Dialog
        open={dialogOffen}
        onClose={sendet ? undefined : () => setDialogOffen(false)}
        aria-labelledby="handrechnung-ausstellen-titel"
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle id="handrechnung-ausstellen-titel">Issue invoice</DialogTitle>
        <DialogContent>
          <Stack spacing={SP.card} sx={{ pt: SP.chip }}>
            <Typography variant="body2">
              This finally issues the invoice for {bestellung}. It consumes an
              invoice number — this cannot be undone.
              {mail
                ? ' The invoice is mailed to the customer.'
                : ' Mail delivery is suppressed — the invoice does not go to the customer.'}
            </Typography>
            {fehler && fehlerFlaeche}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOffen(false)} disabled={sendet}>Cancel</Button>
          <Button
            variant="contained"
            onClick={ausstellen}
            disabled={sendet}
            startIcon={sendet ? <CircularProgress size={20} color="inherit" aria-hidden="true" /> : null}
          >
            Issue invoice
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

// ── N3-A (S5.8) — die Handrechnung OHNE Bestellung (Weg A) ──────────────────
const HAND_OHNE_PFAD = '/api/invoicing/admin/handrechnung/ohne-bestellung'

/** Die zwei Kundentypen (§ 2.7) — der Kundentyp waehlt den Steuerfall mit. */
const HAND_KUNDENTYPEN = [
  { wert: 'b2c', text: 'Private customer' },
  { wert: 'b2b', text: 'Business (without a valid VAT ID)' },
]

/** Die drei Warengruppen der Satzliste (dieselben wie im Rechner). */
const HAND_WARENGRUPPEN = [
  { wert: 'coffee', text: 'Coffee' },
  { wert: 'equipment', text: 'Device' },
  { wert: 'shipping', text: 'Shipping' },
]

/**
 * Die Lieferländer der Handrechnung — **EU-Staaten zuerst, dann Drittländer**, je
 * alphabetisch nach deutschem Namen.
 *
 * 🔴 **Kein Freitext.** Der Steuerfall wird aus dem Land abgeleitet
 * (`_steuerfall_aus_eingabe`): alles ausserhalb der EU ist Ausfuhr mit 0 %. Ein
 * getipptes „Österreich" oder „Deutschland" ergäbe deshalb eine **Ausfuhrrechnung
 * mit 0 %** und verbrauchte eine Nummer. Das Backend weist einen unbekannten Wert
 * mit `422 lieferland:unbekannt` ab (`_lieferland_pruefen`); dieses Auswahlfeld ist
 * die Fläche dazu.
 *
 * 🔴 **Die Liste ist eine Projektion, keine zweite Quelle.** `Betrieb.test.jsx`
 * liest `backend/invoicing/laender.py` (Code **und** deutscher Name) und
 * `backend/tax/k4_erloese.py` (`_VIES_LAENDER` + `GR` = die EU-Menge) und macht
 * diese Datei rot, sobald ein Land fehlt, dazukommt oder die EU-Zuordnung abweicht.
 * `EL` steht neben `GR`, weil beide Codes dasselbe Land meinen und das Backend
 * beide annimmt — der Code im Label unterscheidet sie.
 */
export const HAND_LAENDER = [
  // ── Europäische Union (EU_LAENDER aus backend/tax/k4_erloese.py) ──────────
  { code: 'BE', name: 'Belgium', eu: true },
  { code: 'BG', name: 'Bulgaria', eu: true },
  { code: 'DK', name: 'Denmark', eu: true },
  { code: 'DE', name: 'Germany', eu: true },
  { code: 'EE', name: 'Estonia', eu: true },
  { code: 'FI', name: 'Finland', eu: true },
  { code: 'FR', name: 'France', eu: true },
  { code: 'EL', name: 'Greece', eu: true },
  { code: 'GR', name: 'Greece', eu: true },
  { code: 'IE', name: 'Ireland', eu: true },
  { code: 'IT', name: 'Italy', eu: true },
  { code: 'HR', name: 'Croatia', eu: true },
  { code: 'LV', name: 'Latvia', eu: true },
  { code: 'LT', name: 'Lithuania', eu: true },
  { code: 'LU', name: 'Luxembourg', eu: true },
  { code: 'MT', name: 'Malta', eu: true },
  { code: 'NL', name: 'Netherlands', eu: true },
  { code: 'XI', name: 'Northern Ireland', eu: true },
  { code: 'AT', name: 'Austria', eu: true },
  { code: 'PL', name: 'Poland', eu: true },
  { code: 'PT', name: 'Portugal', eu: true },
  { code: 'RO', name: 'Romania', eu: true },
  { code: 'SE', name: 'Sweden', eu: true },
  { code: 'SK', name: 'Slovakia', eu: true },
  { code: 'SI', name: 'Slovenia', eu: true },
  { code: 'ES', name: 'Spain', eu: true },
  { code: 'CZ', name: 'Czechia', eu: true },
  { code: 'HU', name: 'Hungary', eu: true },
  { code: 'CY', name: 'Cyprus', eu: true },
  // ── Drittländer (LAND_NAMEN aus backend/invoicing/laender.py) ────────────
  { code: 'AU', name: 'Australia', eu: false },
  { code: 'IS', name: 'Iceland', eu: false },
  { code: 'JP', name: 'Japan', eu: false },
  { code: 'CA', name: 'Canada', eu: false },
  { code: 'LI', name: 'Liechtenstein', eu: false },
  { code: 'NO', name: 'Norway', eu: false },
  { code: 'CH', name: 'Switzerland', eu: false },
  { code: 'US', name: 'United States', eu: false },
  { code: 'GB', name: 'United Kingdom', eu: false },
]

/** Das Label eines Landes im Auswahlfeld — Name **und** Code (R16 B2). */
const landLabel = (land) => `${land.name} (${land.code})`

const HAND_LEERER_EMPFAENGER = {
  name: '', street: '', postal_code: '', city: '', country: 'DE', email: '',
}

/**
 * Das Empfänger-Raster der Handrechnung (S9): **zwei Spalten ab `sm`** (600 px),
 * eine darunter.
 *
 * 🔴 `minmax(0, 1fr)` statt `1fr`, und das ist kein Geschmack: `1fr` ist
 *    `minmax(auto, 1fr)`, und `auto` ist die **Mindestbreite des Inhalts**. Ein
 *    Feld mit langem Inhalt („United Kingdom (GB)") zöge seine Spalte damit über
 *    die Rasterbreite hinaus — bei 390 px wäre das ein waagerechter Überlauf.
 *    Mit `minmax(0, …)` darf die Spalte schrumpfen, das Feld schneidet ab.
 *
 * 🔴 Der Deckel `CONTENT_WIDTH.prose` (640) hält die zwei Spalten bei 1440 px
 *    lesbar — ohne ihn zöge das Raster über die ganze Seitenbreite und die zwei
 *    Hälften stünden weit auseinander. Es ist ein **benannter Token**, kein
 *    Literal: `scripts/check_ui_tokens.py` (`raw_page_cap`) verbietet einen
 *    drei- oder vierstelligen `maxWidth` in `sx` (Regel 22).
 */
const EMPFAENGER_RASTER_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
  gap: SP.card,
  maxWidth: CONTENT_WIDTH.prose,
}

/** Ein Feld über die **volle** Rasterbreite — Name, Straße, E-Mail (S9). */
const RASTER_VOLL = { gridColumn: '1 / -1' }

const handLeerePosition = () => ({
  beschreibung: '', warengruppe: 'coffee', menge: '1', einzelbrutto: '', satz: '',
})

/**
 * Der Weg A der „Manuellen Rechnung" (N3-A): **ohne Bestellung** — Empfänger,
 * Kundentyp, Positionen (Warengruppe / Menge / Einzelbrutto / Satz aus der
 * Liste) und Mail an/aus.
 *
 * 🔴 Der Idempotenzschluessel entsteht **bei der Vorschau** und wird erst beim
 * Ausstellen benutzt: zwei Klicks mit demselben Schlüssel ergeben **eine**
 * Nummer. Die Vorschau schreibt nichts.
 *
 * 🔴 **Ein zusätzliches Feld je Position: „Bezeichnung".** § 14 Abs. 4 Nr. 5 UStG
 * verlangt „die Menge und die Art (handelsübliche Bezeichnung) des Gegenstands";
 * `Position.beschreibung` traegt sie ins Dokument. § 2.7 nennt sie nicht — ohne
 * sie entstuende eine Rechnung ohne Leistungsbeschreibung.
 */
function ManuelleRechnungOhneBestellung({ onSchmutzig }) {
  const [empfaenger, setEmpfaenger] = useState(HAND_LEERER_EMPFAENGER)
  const [kundentyp, setKundentyp] = useState('b2c')
  const [mail, setMail] = useState(true)
  const [zeilen, setZeilen] = useState(() => [handLeerePosition()])
  const [saetze, setSaetze] = useState({})
  const [hinweise, setHinweise] = useState([])
  const [warnungen, setWarnungen] = useState([])
  const [summen, setSummen] = useState(null)
  const [steuerfall, setSteuerfall] = useState(null)
  const [ausstellungsdatum, setAusstellungsdatum] = useState(null)
  const [schluessel, setSchluessel] = useState(null)
  const [laedt, setLaedt] = useState(false)
  const [ersteLadung, setErsteLadung] = useState(true)
  const [fehler, setFehler] = useState(null)
  const [erfolg, setErfolg] = useState(null)
  const [dialogOffen, setDialogOffen] = useState(false)
  const [sendet, setSendet] = useState(false)
  // R15A W1: die Ausfuhranzeige des Bedieners (§ 6 Abs. 4 UStG). Nur beim
  // abgeleiteten Steuerfall Ausfuhr sichtbar und Pflicht — eine 0-%-Rechnung ohne
  // Nachweis wäre eine Steuerbefreiung ohne Beleg.
  const [ausfuhrBestaetigt, setAusfuhrBestaetigt] = useState(false)
  // 🔴 R16 W1 (S9-Nachbesserung): ein Pflichtfeld, das noch niemand berührt hat,
  // ist LEER, nicht FALSCH. `mail` ist mit `true` vorbelegt, die Adresse leer —
  // ohne diesen Zustand stünde die rote Zeile beim ersten Rendern da (Basisfoto
  // `rechs9b_basis/1440_hand_ohne_leer`). Die Fachregel bleibt unverändert: der
  // Mailwunsch ohne Adresse sperrt „Issue" weiterhin und nennt seinen Grund.
  const [emailBeruehrt, setEmailBeruehrt] = useState(false)

  const gesperrt = Boolean(erfolg)

  // 🔴 R16 W-A: der Grund am „Load"-Knopf steht sichtbar neben ihm (R16 W2) —
  // dann gehört er per `aria-describedby` an den Knopf gebunden, sonst hat er
  // keinen Programmbezug (Hausform: `DisabledReason` `@dontUseFor`, Vorbild
  // `pages/secondLife/OpenCases.jsx`). Ein deaktivierter Knopf ist nicht
  // fokussierbar; die Bindung ist der Weg, den der Lesefluss allein nicht
  // herstellt. Die `id` lebt nur, solange die Zeile steht.
  const loadGrundId = useId()

  /**
   * 🔴 R16 W1: die abgeleiteten Werte VERWERFEN, wenn sich ihre Grundlage ändert.
   *
   * Steuerfall, Satzliste, Summen und der Idempotenzschlüssel sind aus **Lieferland
   * und Kundentyp** abgeleitet. Blieben sie stehen, behauptete die Fläche eine
   * Ableitung, die nicht mehr gilt (AT-Sätze neben „Inland") — auffliegend erst beim
   * Ausstellen mit 422. Die gewählten Sätze je Position gehen mit: sie kommen aus
   * der Liste des alten Steuerfalls und sind in der neuen womöglich unzulässig.
   */
  const verwirfAbgeleitetes = () => {
    setSaetze({}); setHinweise([]); setWarnungen([]); setSummen(null)
    setSteuerfall(null); setAusstellungsdatum(null); setSchluessel(null)
    setAusfuhrBestaetigt(false)
    setZeilen((alt) => alt.map((z) => ({ ...z, satz: '' })))
  }

  const setEmpfaengerFeld = (feld, wert) => {
    // 🔴 Nur das **Lieferland** ist eine Ableitungsgrundlage (R16 W1) — es waehlt
    // den Steuerfall. Die PLZ gehoert zum Empfaengerblock und wird **vor** „Laden"
    // gefuellt; sie mitzuverwerfen liesse den Bediener die Satzliste zweimal holen.
    if (feld === 'country') verwirfAbgeleitetes()
    setEmpfaenger((alt) => ({ ...alt, [feld]: wert }))
  }

  const setKundentypUndVerwerfen = (wert) => {
    verwirfAbgeleitetes()
    setKundentyp(wert)
  }

  const setZeile = (index, feld, wert) => setZeilen((alt) => alt.map(
    (z, i) => (i === index ? { ...z, [feld]: wert } : z)))

  const alsFehler = (e) => {
    const daten = e?.response?.data || {}
    const code = daten.error
    const text = code
      ? (HAND_FEHLER_TEXT[code] || HAND_FEHLER_ALLGEMEIN)
      : (e?.message || 'Unknown error')
    return {
      text,
      code: code && !HAND_FEHLER_TEXT[code] ? code : null,
      gruende: [
        ...(daten.text ? [daten.text] : []),
        ...(daten.gruende || []),
        ...(daten.texte || []),
      ],
    }
  }

  const positionen = () => zeilen.map((z) => ({
    beschreibung: z.beschreibung,
    warengruppe: z.warengruppe,
    menge: z.menge,
    einzelbrutto: z.einzelbrutto,
    satz: z.satz,
  }))

  const rumpf = (mitPositionen) => ({
    empfaenger,
    kundentyp,
    mail,
    ausfuhrnachweis_bestaetigt: ausfuhrBestaetigt,
    ...(schluessel ? { idempotenzschluessel: schluessel } : {}),
    ...(mitPositionen ? { positionen: positionen() } : {}),
  })

  const ruf = async (pfad, mitPositionen) => {
    setLaedt(true); setFehler(null); setErfolg(null)
    try {
      const { data } = await api.post(`${HAND_OHNE_PFAD}/${pfad}`, rumpf(mitPositionen))
      return data
    } catch (e) {
      setFehler(alsFehler(e))
      return null
    } finally {
      setLaedt(false); setErsteLadung(false)
    }
  }

  /** „Laden" holt die Satzliste (und den Idempotenzschluessel) — ohne Positionen. */
  const laden = async () => {
    setSaetze({}); setHinweise([]); setWarnungen([]); setSummen(null)
    const data = await ruf('vorschau', false)
    if (!data) return
    setSaetze(data.saetze || {})
    setHinweise(data.hinweise || [])
    setWarnungen(data.warnungen || [])
    setSteuerfall(data.steuerfall || null)
    setAusstellungsdatum(data.ausstellungsdatum || null)
    setSchluessel(data.idempotenzschluessel || null)
  }

  const vorschau = async () => {
    setSummen(null)
    const data = await ruf('vorschau', true)
    if (!data) return
    setSaetze(data.saetze || {})
    setHinweise(data.hinweise || [])
    setWarnungen(data.warnungen || [])
    setSteuerfall(data.steuerfall || null)
    setAusstellungsdatum(data.ausstellungsdatum || null)
    setSchluessel(data.idempotenzschluessel || null)
    setSummen(data.ergebnis ? data.ergebnis.summen : null)
  }

  const ausstellen = async () => {
    setSendet(true)
    const data = await ruf('ausstellen', true)
    setSendet(false)
    if (data) { setErfolg(data); setDialogOffen(false) }
  }

  const weitereHandrechnung = () => {
    setEmpfaenger(HAND_LEERER_EMPFAENGER); setKundentyp('b2c'); setMail(true)
    setZeilen([handLeerePosition()]); setSaetze({}); setHinweise([]); setWarnungen([])
    setSummen(null); setSteuerfall(null); setAusstellungsdatum(null); setSchluessel(null)
    setAusfuhrBestaetigt(false)
    setEmailBeruehrt(false)
    setFehler(null); setErfolg(null); setErsteLadung(true)
  }

  const zeilenVollstaendig = zeilen.length > 0 && zeilen.every(
    (z) => String(z.beschreibung).trim() !== '' && String(z.satz).trim() !== ''
      && String(z.einzelbrutto).trim() !== '')
  const empfaengerVollstaendig = ['name', 'street', 'postal_code', 'city', 'country']
    .every((feld) => String(empfaenger[feld] || '').trim() !== '')
  const bereit = !gesperrt && empfaengerVollstaendig && zeilenVollstaendig
  /** 🔴 R16 B1: ein Mailwunsch ohne Adresse kann nicht erfüllt werden. */
  const mailOhneAdresse = mail && String(empfaenger.email || '').trim() === ''
  /**
   * 🔴 R16 W1 (S9-Nachbesserung): DERSELBE Befund, aber erst wenn er etwas
   * aussagt — nach dem Verlassen des Feldes oder wenn der Empfänger sonst
   * vollständig ist. `mailOhneAdresse` bleibt unverändert die Sperre; nur der
   * Zeitpunkt der Darstellung hängt an diesem Wert.
   */
  const mailFehlerZeigen = mailOhneAdresse && (emailBeruehrt || empfaengerVollstaendig)
  /** 🔴 R15A W1: die Ausfuhr braucht die Bestätigung des Bedieners (§ 6 Abs. 4 UStG). */
  const ausfuhrOhneNachweis = steuerfall === 'export' && !ausfuhrBestaetigt
  /**
   * Die Ausstellung — strenger als die Vorschau: sie braucht den Schlüssel, die
   * Adresse für die Mail und (beim Ausfuhrfall) den Ausfuhrnachweis.
   */
  const ausstellbar = bereit && Boolean(schluessel)
    && !mailOhneAdresse && !ausfuhrOhneNachweis
  /**
   * 🔴 R15A W2: ein Unternehmer im EU-Ausland wird mit **deutscher** USt berechnet
   * (§ 2.8 Nr. 3) — diese Fläche kennt keine USt-IdNr. und kann deshalb keine
   * innergemeinschaftliche Lieferung ausstellen. Das gehört in den Dialog, nicht in
   * eine Fußnote: der Unterschied ist eine Steuer, keine Bequemlichkeit.
   */
  const b2bImEuAusland = kundentyp === 'b2b' && steuerfall === 'domestic'
    && String(empfaenger.country || '').trim().toUpperCase() !== 'DE'

  // 🔴 R16 W2: meldet ungesicherte Eingaben an den Weg-Schalter. `country` zählt
  // NICHT — es ist mit `DE` vorbelegt und damit immer „gefüllt".
  const schmutzig = Boolean(schluessel) || Boolean(erfolg)
    || zeilen.some((z) => z.beschreibung || z.einzelbrutto || z.satz)
    || ['name', 'street', 'postal_code', 'city', 'email']
      .some((feld) => String(empfaenger[feld] || '').trim() !== '')
  useEffect(() => { onSchmutzig?.(schmutzig) }, [schmutzig, onSchmutzig])

  const fehlerFlaeche = (
    <Alert severity="error">
      <AlertTitle>{fehler?.text}</AlertTitle>
      {fehler?.code && <Gedaempft>{`Code: ${fehler.code}`}</Gedaempft>}
      {fehler?.gruende.length > 0 && (
        <Box component="ul" sx={{ m: 0, pl: SP.md }}>
          {fehler.gruende.map((g) => <li key={g}>{g}</li>)}
        </Box>
      )}
    </Alert>
  )

  return (
    <Stack spacing={SP.md}>
      <Erklaerung>
        Issues an invoice without an order by hand — direct sale,
        phone or mail order. The tax case is derived from the delivery country and
        customer type. The preview writes nothing; only “Issue”
        assigns a number.
      </Erklaerung>

      <Gruppe titel="Recipient">
        <Erklaerung>
          Name and complete address are mandatory details (§ 14 Abs. 4 Nr. 1 UStG).
          Delivery country and postal code also determine the tax case.
        </Erklaerung>
        {/* S9: das Empfänger-Raster. Die Reihenfolge ist die Reihenfolge des
            Formulars und ändert sich beim „Load" NICHT (Akzeptanz 2) — „Load"
            setzt nur Werte, es hängt kein Feld um. */}
        <Box sx={EMPFAENGER_RASTER_SX} data-testid="handrechnung-empfaenger">
          <TextField size="small" sx={RASTER_VOLL} label="Name" value={empfaenger.name}
                     onChange={(e) => setEmpfaengerFeld('name', e.target.value)}
                     inputProps={{ 'aria-label': 'Name' }} disabled={gesperrt} />
          <TextField size="small" sx={RASTER_VOLL} label="Street" value={empfaenger.street}
                     onChange={(e) => setEmpfaengerFeld('street', e.target.value)}
                     inputProps={{ 'aria-label': 'Street' }} disabled={gesperrt} />
          <TextField size="small" label="Postal code" value={empfaenger.postal_code}
                     onChange={(e) => setEmpfaengerFeld('postal_code', e.target.value)}
                     inputProps={{ 'aria-label': 'Postal code' }} disabled={gesperrt} />
          <TextField size="small" label="City" value={empfaenger.city}
                     onChange={(e) => setEmpfaengerFeld('city', e.target.value)}
                     inputProps={{ 'aria-label': 'City' }} disabled={gesperrt} />
          {/* 🔴 R16 B2: ein AUSWAHLFELD, kein Freitext. Ein getipptes „Österreich"
              wäre sonst still zur Ausfuhr mit 0 % geworden. */}
          <TextField size="small" select label="Delivery country" value={empfaenger.country}
                     onChange={(e) => setEmpfaengerFeld('country', e.target.value)}
                     SelectProps={{ native: true }}
                     inputProps={{ 'aria-label': 'Delivery country' }} disabled={gesperrt}>
            <optgroup label="European Union">
              {HAND_LAENDER.filter((l) => l.eu).map((l) => (
                <option key={l.code} value={l.code}>{landLabel(l)}</option>
              ))}
            </optgroup>
            <optgroup label="Third countries">
              {HAND_LAENDER.filter((l) => !l.eu).map((l) => (
                <option key={l.code} value={l.code}>{landLabel(l)}</option>
              ))}
            </optgroup>
          </TextField>
          {/* S9: der Kundentyp steht neben dem Lieferland — beide leiten den
              Steuerfall mit ab, und ihre Nachbarschaft macht das sichtbar. */}
          <TextField size="small" select label="Customer type" value={kundentyp}
                     onChange={(e) => setKundentypUndVerwerfen(e.target.value)}
                     inputProps={{ 'aria-label': 'Customer type' }} disabled={gesperrt}>
            {HAND_KUNDENTYPEN.map((k) => (
              <MenuItem key={k.wert} value={k.wert}>{k.text}</MenuItem>
            ))}
          </TextField>
          <TextField size="small" sx={RASTER_VOLL} label="Email (for the mail)" value={empfaenger.email}
                     onChange={(e) => setEmpfaengerFeld('email', e.target.value)}
                     onBlur={() => setEmailBeruehrt(true)}
                     error={mailFehlerZeigen}
                     helperText={mailFehlerZeigen
                       ? 'Without an email address the invoice cannot be sent — remove the checkmark or enter an address.'
                       : ' '}
                     inputProps={{ 'aria-label': 'Email (for the mail)' }} disabled={gesperrt} />
        </Box>
      </Gruppe>

      <Stack direction="row" spacing={SP.chip} alignItems="center" flexWrap="wrap">
        {/* 🔴 R16 W3: „Laden" holt die Satzliste des abgeleiteten Steuerfalls — ohne
            vollständigen Empfänger liefe es in `empfaenger:unvollstaendig`, also in
            einen Fehler zu einem anderen Feld als dem, auf das der Knopf zielt. */}
        {/* 🔴 R16 W2 (S9-Nachbesserung): der unvollständige Empfänger stand hier
            ZWEIMAL — als Tooltip und als sichtbare Zeile darunter, in zwei
            Formulierungen. Eine Quelle bleibt: die SICHTBARE Zeile (sie erreicht
            das Telefon, wo es keinen Hover gibt). Der Halter behält seinen Grund
            für den anderen Fall — eine bereits ausgestellte Rechnung. */}
        <DisabledReason
          reason={laedt
            ? null
            : gesperrt ? 'This manual invoice has already been issued.' : null}
        >
          <Button variant="outlined" size="small" onClick={laden}
                  disabled={laedt || gesperrt || !empfaengerVollstaendig}
                  aria-describedby={!empfaengerVollstaendig ? loadGrundId : undefined}>
            Load
          </Button>
        </DisabledReason>
        {!empfaengerVollstaendig && (
          <Gedaempft id={loadGrundId}>
            “Load” is blocked while the recipient is incomplete.
          </Gedaempft>
        )}
      </Stack>

      {fehler && !dialogOffen && fehlerFlaeche}

      {laedt && ersteLadung && (
        <Paper elevation={0} sx={PANEL_SX} data-testid="handrechnung-ohne-laden" aria-busy="true">
          <Stack spacing={SP.tight}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} data-testid="handrechnung-ohne-skelett" variant="rounded" height={32} />
            ))}
          </Stack>
        </Paper>
      )}
      {laedt && !ersteLadung && <LinearProgress aria-label="Updating" />}

      {(steuerfall || ausstellungsdatum) && (
        <Gruppe titel="Dates">
          <Erklaerung>
            The tax case is derived from the delivery country and customer type; the
            issuance date is today (invoice date).
          </Erklaerung>
          {/* S9: dieselbe Rasterbreite wie der Empfänger, und jede Lesezeile hat
              ihre FESTE Zelle — der Steuerfall Spalte 1, das Ausstellungsdatum
              Spalte 2. Damit springt nichts, wenn nur einer der beiden Werte
              dasteht: die Zelle bleibt, nur der Nachbar fehlt.
              🔴 Die Spalte wird nur ab `sm` gesetzt: auf einer Spalte (`xs`)
              erzeugte `gridColumn: 2` eine ZWEITE, implizite Spalte und damit
              den waagerechten Überlauf, den das Raster gerade vermeiden soll. */}
          <Box sx={EMPFAENGER_RASTER_SX}>
            {steuerfall && (
              <Box sx={{ gridColumn: { xs: 'auto', sm: 1 } }}>
                <Gedaempft>Tax case (derived)</Gedaempft>
                <Typography component="div">{STEUERFALL_LABEL[steuerfall] || steuerfall}</Typography>
              </Box>
            )}
            {ausstellungsdatum && (
              <Box sx={{ gridColumn: { xs: 'auto', sm: 2 } }}>
                <Gedaempft>Issuance date</Gedaempft>
                <Typography component="div">
                  {formatBerlinDate(ausstellungsdatum, { dateOnly: true })}
                </Typography>
              </Box>
            )}
          </Box>
        </Gruppe>
      )}

      {/* 🔴 R15A W1 — § 6 Abs. 4 UStG: die Steuerfreiheit der Ausfuhr setzt einen
          Ausfuhrnachweis voraus. Nur beim abgeleiteten Steuerfall Ausfuhr, und
          Pflicht vor „Ausstellen" (Backend: 422 `ausfuhr:nachweis_unbestaetigt`). */}
      {steuerfall === 'export' && (
        <FormControlLabel
          control={(
            <Checkbox size="small" checked={ausfuhrBestaetigt}
                      onChange={(e) => setAusfuhrBestaetigt(e.target.checked)}
                      disabled={gesperrt} />
          )}
          label="A proof of export is present or will be obtained (§ 6 Abs. 4 UStG)"
        />
      )}

      <Gruppe titel="Items">
        <Erklaerung>
          Description, product group, quantity, gross unit price and rate are set by the operator —
          the rate comes from the list of the derived tax case (first “Load”).
        </Erklaerung>
        <Paper elevation={0} sx={{ ...PANEL_SX, overflowX: 'auto' }}>
          <Table size="small" aria-label="Items">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell>Product group</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Gross unit</TableCell>
                <TableCell>Rate</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {zeilen.map((z, i) => (
                <TableRow key={`zeile-${i}`}>
                  <TableCell>
                    <TextField size="small" value={z.beschreibung}
                               onChange={(e) => setZeile(i, 'beschreibung', e.target.value)}
                               inputProps={{ 'aria-label': `Description ${i + 1}` }}
                               disabled={gesperrt} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" select value={z.warengruppe}
                               onChange={(e) => setZeile(i, 'warengruppe', e.target.value)}
                               SelectProps={{ native: true }}
                               inputProps={{ 'aria-label': `Product group ${i + 1}` }}
                               disabled={gesperrt}>
                      {HAND_WARENGRUPPEN.map((w) => (
                        <option key={w.wert} value={w.wert}>{w.text}</option>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField size="small" value={z.menge}
                               onChange={(e) => setZeile(i, 'menge', e.target.value)}
                               inputProps={{ 'aria-label': `Quantity ${i + 1}` }}
                               disabled={gesperrt} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" value={z.einzelbrutto}
                               onChange={(e) => setZeile(i, 'einzelbrutto', e.target.value)}
                               inputProps={{ 'aria-label': `Gross unit ${i + 1}` }}
                               disabled={gesperrt} />
                  </TableCell>
                  <TableCell>
                    {/* 🔴 `native` (R16 B1): nur ein echtes `<select>` traegt
                        `aria-label` selbst — bei einem MUI-`Select` landet
                        `inputProps` auf dem versteckten, `aria-hidden`-Feld. */}
                    <TextField size="small" select value={z.satz}
                               onChange={(e) => setZeile(i, 'satz', e.target.value)}
                               SelectProps={{ native: true }}
                               inputProps={{ 'aria-label': `Rate ${i + 1}` }}
                               disabled={gesperrt}>
                      <option value="">Choose rate</option>
                      {(saetze[z.warengruppe] || []).map((s) => (
                        <option key={s} value={s}>{`${s} %`}</option>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <DisabledReason
                      reason={gesperrt
                        ? 'This manual invoice has already been issued.'
                        : zeilen.length === 1 ? 'At least one line must remain.' : null}
                    >
                      <Button size="small" color="error" onClick={() => setZeilen(
                        (alt) => alt.filter((_z, j) => j !== i))}
                              disabled={gesperrt || zeilen.length === 1}>
                        {`Remove ${i + 1}`}
                      </Button>
                    </DisabledReason>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
        <Box>
          <Button size="small" variant="outlined"
                  onClick={() => setZeilen((alt) => [...alt, handLeerePosition()])}
                  disabled={gesperrt}>
            Add item
          </Button>
        </Box>
      </Gruppe>

      {(hinweise.length > 0 || warnungen.length > 0) && (
        <Gruppe titel="Notices">
          {hinweise.map((h) => <Alert key={h} severity="info" icon={false}>{h}</Alert>)}
          {warnungen.map((w) => <Alert key={w} severity="warning">{w}</Alert>)}
        </Gruppe>
      )}

      {summen && (
        <Gruppe titel="Preview (writes nothing)">
          <Gedaempft>
            {`Draft ${ENTWURF_NUMMER} · net ${summen.netto} · tax ${summen.steuer} · gross ${summen.brutto}`}
          </Gedaempft>
        </Gruppe>
      )}

      {erfolg && (
        <Alert severity="success">
          <AlertTitle>{`Invoice ${erfolg.invoice_number} issued`}</AlertTitle>
          <RechnungsLink id={erfolg.invoice_id} />
          {/* 🔴 R16 B1: die Fläche sagt, was WIRKLICH geschah — `mail` ist das
              Ergebnis des Versands, nicht der Wunsch des Bedieners. */}
          <MailErgebnis erfolg={erfolg} />
        </Alert>
      )}

      <Stack direction="row" spacing={SP.chip} alignItems="center" flexWrap="wrap">
        <FormControlLabel
          control={(
            <Checkbox size="small" checked={mail}
                      onChange={(e) => setMail(e.target.checked)} disabled={gesperrt} />
          )}
          label="Send invoice to the customer"
        />
        <DisabledReason
          reason={laedt
            ? null
            : gesperrt ? 'This manual invoice has already been issued.'
              : !empfaengerVollstaendig ? 'Complete the recipient address first.'
                : !zeilenVollstaendig ? 'Complete every line first.' : null}
        >
          <Button variant="outlined" size="small" onClick={vorschau} disabled={laedt || !bereit}>
            Preview
          </Button>
        </DisabledReason>
        <DisabledReason
          reason={laedt
            ? null
            : gesperrt ? 'This manual invoice has already been issued.'
              : !empfaengerVollstaendig ? 'Complete the recipient address first.'
                : !zeilenVollstaendig ? 'Complete every line first.'
                  : !schluessel ? 'Load the tax case first — no tax key has been derived yet.'
                    : mailOhneAdresse ? 'Add the recipient email or switch the mail option off.'
                      : ausfuhrOhneNachweis ? 'Confirm the export evidence first.' : null}
        >
          <Button variant="contained" size="small" onClick={() => setDialogOffen(true)}
                  disabled={laedt || !ausstellbar}>
            Issue
          </Button>
        </DisabledReason>
        {gesperrt && (
          <Button variant="outlined" size="small" onClick={weitereHandrechnung}>
            Another manual invoice
          </Button>
        )}
      </Stack>

      <Dialog open={dialogOffen} onClose={sendet ? undefined : () => setDialogOffen(false)}
              aria-labelledby="handrechnung-ohne-ausstellen-titel" fullWidth maxWidth="xs">
        <DialogTitle id="handrechnung-ohne-ausstellen-titel">Issue invoice</DialogTitle>
        <DialogContent>
          <Stack spacing={SP.card} sx={{ pt: SP.chip }}>
            <Typography variant="body2">
              This finally issues the invoice for {empfaenger.name || 'the recipient'}. It consumes an
              invoice number — this cannot be undone.
              {mail
                ? ' The invoice is mailed to the customer.'
                : ' Mail delivery is suppressed — the invoice does not go to the customer.'}
            </Typography>
            {b2bImEuAusland && (
              <Typography variant="body2">
                Calculated with German VAT. If the customer has a valid VAT ID,
                this is an intra-Community supply — use the intra-Community release
                for that, not this area.
              </Typography>
            )}
            {steuerfall === 'export' && (
              <Typography variant="body2">
                Export: it is confirmed that a proof of export is present or will be
                obtained (§ 6 Abs. 4 UStG).
              </Typography>
            )}
            {fehler && fehlerFlaeche}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOffen(false)} disabled={sendet}>Cancel</Button>
          <Button variant="contained" onClick={ausstellen} disabled={sendet}
                  startIcon={sendet ? <CircularProgress size={20} color="inherit" aria-hidden="true" /> : null}>
            Issue invoice
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

/**
 * Die Wahl zwischen **Weg B** (zu einer Bestellung) und **Weg A** (ohne
 * Bestellung) — EIN Abschnitt mit beiden Flächen (§ 2.7); Vorgabe ist Weg B.
 */
function ManuelleRechnungWahl() {
  const [weg, setWeg] = useState('bestellung')
  // 🔴 R16 W2: der Wechsel hängt die jeweils andere Fläche ab — er verwirft
  // Empfänger und Positionen **ohne Warnung**. Beide Flächen melden über
  // `onSchmutzig`, ob ungesicherte Eingaben dastehen; nur dann fragt der Wechsel
  // nach. Das gewählte Muster ist die **Rückfrage** (nicht das Bewahren): sie kostet
  // zwei Knöpfe und keine zweite Zustandsführung.
  const [schmutzig, setSchmutzig] = useState(false)
  const [ziel, setZiel] = useState(null)
  const merkeSchmutzig = useCallback((wert) => setSchmutzig(wert), [])

  const wechsle = (neu) => {
    if (neu === weg) return
    if (schmutzig) { setZiel(neu); return }
    setWeg(neu)
  }
  const wechsleJetzt = () => {
    setWeg(ziel); setZiel(null); setSchmutzig(false)
  }

  return (
    <Stack spacing={SP.md}>
      {/* 🔴 S8 — die Wegewahl ist ein ZWEIFACH-SCHALTER, kein Klappmenü: zwei
          Wege, beide sichtbar, einer gewählt. Ein Menü verbirgt den zweiten
          hinter einem Klick und gibt dem gewählten keine Fläche.
          🔴 **Die Gruppe steht ausserhalb eines dichten Kontexts** und erbt
          damit den 44-px-Boden aus dem Theme (`MuiToggleButton`,
          `TOUCH_TARGET_COARSE_ACTIVE`) — nichts nachgebaut, kein Literal.
          🔴 **Der Name der Gruppe bleibt „Path"**: er ist die Adresse, unter der
          Tests und Screenreader die Wahl finden.
          🔴 Die Rückfrage beim Wechsel MIT Eingaben bleibt unverändert — der
          Schalter ruft denselben `wechsle`. */}
      <Box>
        <ToggleButtonGroup
          exclusive
          value={weg}
          onChange={(e, neu) => { if (neu) wechsle(neu) }}
          aria-label="Path"
          sx={{ alignSelf: 'flex-start' }}
        >
          <ToggleButton value="bestellung">To an order</ToggleButton>
          <ToggleButton value="ohne">Without an order</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" component="p" sx={{ color: 'text.secondary', mt: SP.chip }}>
          To an order: the items come from an existing order.
          {' '}Without an order: free items, no order reference.
        </Typography>
      </Box>
      {weg === 'bestellung'
        ? <ManuelleRechnung onSchmutzig={merkeSchmutzig} />
        : <ManuelleRechnungOhneBestellung onSchmutzig={merkeSchmutzig} />}

      <Dialog open={ziel !== null} onClose={() => setZiel(null)}
              aria-labelledby="handrechnung-weg-titel" fullWidth maxWidth="xs">
        <DialogTitle id="handrechnung-weg-titel">Switch path?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The switch discards the inputs of this area — they are not
            saved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setZiel(null)}>Cancel</Button>
          <Button variant="contained" onClick={wechsleJetzt}>Switch</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

// ── S1 — das Band „Needs attention" ────────────────────────────────────────
//
// 🔴 **Sechs Quellen, zwei davon neu** (§1.6). Die vier geladenen Antworten
//    tragen Lauf, Nummernkreis, Steuerabweichung, IgL-Nachweis und die drei
//    Abgleich-Klassen; die Arbeitslisten-Zeilen kommen aus ZWEI zusätzlichen,
//    GEFILTERTEN Aufrufen (`/kennzahlen`, `/warnungen`, beide ohne Parameter).
//    Ohne sie stünde am Go-live-Morgen „Needs attention" mit Zeilen da, die an
//    Testbelegen hängen und die niemand je beheben kann.
//
// 🔴 **Eine Quelle hat geantwortet, wenn (F5):** Eingabe nicht `null`, sie lädt
//    nicht, sie hat keinen Fehler **und** `daten !== null`. `useLesen(null)`
//    ergibt eine `unknown`-Zeile mit dem Grund — kein „nichts offen".
//
// 🔴 **Kein Deckel, kein Aufklapper.** Ein geldnaher Befund hinter „+n more"
//    wäre genau das, was die Regel verbietet (`NoticeSlot.@dontUseFor`).

/** Wie viele Quellen das Band liest — die Zahl steht im Ladesatz. */
export const BAND_QUELLEN = 6

/** Der Name der FLÄCHE je Quelle (nie der Endpunkt) — er steht in der `unknown`-Zeile. */
export const BAND_FLAECHE = {
  kennzahlen: 'Open blocks and refund decisions',
  warnungen: 'Tax blocks',
  tagesabschluss: 'Daily close',
  steuerbeobachtung: 'Tax monitoring',
  nummernkreise: 'Number ranges',
  igl: 'Proofs of arrival',
}

/** Der Grund einer `unknown`-Zeile, wenn die EINGABE ungültig war (statt eines Fehlers). */
export const BAND_EINGABE_GRUND = {
  tagesabschluss: 'the date is not valid',
  steuerbeobachtung: 'the date range is not valid',
}

/**
 * Die Anker-Ziele, die im ZUGEKLAPPTEN „Month close" liegen (S8).
 *
 * Ein Sprung dorthin muss den Abschnitt vorher aufklappen — sonst steht er auf
 * `visibility: hidden` und tut sichtbar nichts. „Daily close" und der
 * Refund-Reiter sind nicht dabei.
 */
export const MONAT_KINDER = ['nummernkreise', 'steuerbeobachtung', 'igl']

/** Das Ziel eines Ankers — `null` heißt: kein Anker (die Zeile nennt nur den Ausfall). */
export const BAND_ZIEL_LABEL = {
  'daily-close': 'Daily close',
  refunds: 'Refund decisions',
  nummernkreise: 'Number ranges',
  steuerbeobachtung: 'Tax monitoring',
  igl: 'Proofs of arrival',
}

const SCHWERE_PILL = {
  blocking: { tone: 'err', label: 'Blocking' },
  warning: { tone: 'warn', label: 'Warning' },
  unknown: { tone: 'warn', label: 'Unknown' },
  info: { tone: 'info', label: 'Info' },
}
const SCHWERE_RANG = { blocking: 0, warning: 1, unknown: 2, info: 3 }

const zahlOderNull = (wert) => (Number.isFinite(wert) ? wert : null)

/** Die Zahl EINER Warnklasse; fehlt sie, ist das `null` — nie 0 (Regel 17 §3b). */
const klasseZahl = (daten, klasse) => {
  const treffer = (daten?.klassen || []).find((z) => z?.klasse === klasse)
  return zahlOderNull(treffer?.anzahl)
}

/** „1 open block“ / „n open blocks“ — mit dem Steuer-Nebensatz nur bei `m > 0`. */
const sperrenSatz = (n, m) => (
  `${n} ${n === 1 ? 'open block' : 'open blocks'}${m > 0 ? `, ${m} of them tax` : ''}`
)
const steuerSatz = (m) => `${m} ${m === 1 ? 'tax block' : 'tax blocks'}`
const mehrzahl = (n, eins, viele) => `${n} ${n === 1 ? eins : viele}`

/**
 * Der Name eines Nummernkreises im Satz — dieselbe Beschriftung wie seine Zeile
 * in der Tabelle (`ART_LABEL`, `Jahr`), damit Band und Abschnitt dasselbe meinen.
 */
const kreisName = (z) => `${ART_LABEL[z.document_type] || z.document_type || STRICH} ${ganzzahl(z.jahr)}`

/**
 * Die Zeilen des Bandes aus den sechs Quellen. Rein — kein React, keine Uhr.
 *
 * `quellen` ist je Quelle `{ daten, laedt, fehler, eingabeFehler }`.
 * Die Reihenfolge ist stabil nach Schwere (Blocking → Warning → Unknown → Info);
 * innerhalb einer Schwere bleibt die Reihenfolge der Quellen.
 */
export function bandZeilen(quellen) {
  const k = quellen.kennzahlen
  const w = quellen.warnungen
  const a = quellen.abschluss
  const s = quellen.beobachtung
  const n = quellen.kreise
  const i = quellen.igl
  const zeilen = []

  // (1) Offene Sperren — EINE Zeile, die Steuersperren als Nebensatz.
  //     `plausibilitaet` ist eine TEILMENGE von `offene_sperren` (`m = n` erlaubt);
  //     zwei Zeilen lüden zum Addieren ein, und die Summe wäre falsch.
  const sperren = k.geantwortet ? zahlOderNull(k.daten?.offene_sperren) : null
  const steuer = w.geantwortet ? klasseZahl(w.daten, 'plausibilitaet') : null
  if (sperren !== null) {
    if (steuer !== null && steuer > sperren) {
      // Zwei Antworten aus verschiedenen Ständen: die Steuerzahl steht allein,
      // ohne Gesamtzahl. Nichts wird geklemmt, nichts geschätzt.
      if (steuer > 0) zeilen.push({ schwere: 'blocking', satz: steuerSatz(steuer), ziel: 'daily-close' })
    } else if (sperren > 0) {
      zeilen.push({
        schwere: 'blocking',
        satz: sperrenSatz(sperren, steuer),
        ziel: 'daily-close',
        // R3-W7: die Erklärung steht SICHTBAR in der Zeile, nicht nur im `title`.
        hinweis: 'test invoices excluded; “Open blocks, all invoices” counts them',
      })
    }
  } else if (steuer !== null && steuer > 0) {
    zeilen.push({ schwere: 'blocking', satz: steuerSatz(steuer), ziel: 'daily-close' })
  }

  // (2) Refund decisions — eigene Zeile, Sprung auf den Reiter (kein Beleg-Link).
  if (k.geantwortet) {
    const refund = zahlOderNull(k.daten?.refund_offen)
    if (refund > 0) {
      zeilen.push({
        schwere: 'warning',
        satz: mehrzahl(refund, 'open refund decision', 'open refund decisions'),
        ziel: 'refunds',
      })
    }
  }

  // (3) Zahlungsstörung (Dispute) — Blocking, aus der Warnklasse `dispute`.
  if (w.geantwortet) {
    const dispute = klasseZahl(w.daten, 'dispute')
    if (dispute > 0) {
      zeilen.push({
        schwere: 'blocking',
        satz: mehrzahl(dispute, 'open payment dispute', 'open payment disputes'),
        ziel: 'daily-close',
      })
    }
  }

  // (4)–(7) Daily close: Läufe und die drei Abgleich-Klassen.
  if (a.geantwortet) {
    for (const lauf of (a.daten?.laeufe || [])) {
      const takt = zahlOderNull(lauf?.takt_sekunden)
      const alter = zahlOderNull(lauf?.alter_sekunden)
      const name = LAUF_LABEL[lauf?.kind] || lauf?.kind || STRICH
      // 🔴 Ein gescheiterter Lauf ist ein Befund, kein Altersproblem — und einer
      //    je Lauf: dieselbe Zeile zweimal wäre eine Verdopplung.
      if (lauf?.status === 'error') {
        zeilen.push({ schwere: 'warning', satz: `${name}: last run failed`, ziel: 'daily-close' })
      } else if (takt !== null && alter !== null && alter > 2 * takt) {
        zeilen.push({ schwere: 'warning', satz: `${name}: last run is older than two cycles`, ziel: 'daily-close' })
      }
    }
    const klassen = a.daten?.bb?.abgleich?.klassen
    // 🔴 Eine fehlende Klasse (`null`) ist NIE 0 (`betrieb_routes.py:208-215`).
    for (const [key, schwere] of [['forge_ohne_bb', 'warning'], ['betragsabweichung', 'warning'], ['bb_ohne_forge', 'info']]) {
      const wert = zahlOderNull(klassen?.[key])
      if (wert === null || wert <= 0) continue
      zeilen.push({ schwere, satz: `${ABGLEICH_KLASSE_LABEL[key]}: ${wert}`, ziel: 'daily-close' })
    }
  }

  // (8) Nummernkreis-Lücke — beide Richtungen, der Wert bleibt stehen (auch negativ).
  if (n.geantwortet) {
    for (const z of (n.daten?.kreise || [])) {
      const luecke = zahlOderNull(z?.luecke)
      if (luecke === null || luecke === 0) continue
      zeilen.push({ schwere: 'warning', satz: `Number range ${kreisName(z)}: gap of ${luecke}`, ziel: 'nummernkreise' })
    }
  }

  // (9) Steuerabweichung — derselbe Zeitraum wie der Abschnitt (kein dritter Aufruf).
  if (s.geantwortet) {
    const abweichungen = Array.isArray(s.daten?.abweichungen) ? s.daten.abweichungen.length : 0
    if (abweichungen > 0) {
      zeilen.push({
        schwere: 'blocking',
        satz: `${mehrzahl(abweichungen, 'tax deviation', 'tax deviations')} in the selected period`,
        ziel: 'steuerbeobachtung',
      })
    }
  }

  // (10) IgL-Nachweis offen oder abgelehnt.
  if (i.geantwortet) {
    const offen = Array.isArray(i.daten?.nachweise) ? i.daten.nachweise.length : 0
    if (offen > 0) {
      zeilen.push({
        schwere: 'warning',
        satz: mehrzahl(offen, 'proof of arrival open or rejected', 'proofs of arrival open or rejected'),
        ziel: 'igl',
      })
    }
  }

  // (11) Eine benannte `unknown`-Zeile je Quelle, die NICHT geantwortet hat.
  for (const [key, q] of Object.entries({
    kennzahlen: k, warnungen: w, tagesabschluss: a, steuerbeobachtung: s, nummernkreise: n, igl: i,
  })) {
    if (q.geantwortet) continue
    const grund = (q.eingabeFehler && BAND_EINGABE_GRUND[key]) || 'the request failed'
    zeilen.push({ schwere: 'unknown', satz: `${BAND_FLAECHE[key]}: unknown, ${grund}.`, ziel: null })
  }

  return zeilen
    .map((z, i) => ({ ...z, rang: SCHWERE_RANG[z.schwere], index: i }))
    .sort((x, y) => (x.rang - y.rang) || (x.index - y.index))
    .map(({ rang, index, ...z }) => z)
}

/**
 * Das Band „Needs attention" — eine Region, eine Zeile je Befund, kein Deckel.
 * Rendert NICHTS, wenn es nichts zu melden gibt (kein leerer Rahmen, S1-Akz. 1).
 *
 * 🔴 **Auf 390 ist die Zeile EINZEILIG** (§2.3, Absatz „390": „das Band ist
 *    eine Liste einzeiliger Befunde"). Der Bau stand bis zum 2026-11-01 auf
 *    `direction={{ xs: 'column', sm: 'row' }}` — Pille, Satz und Knopf in drei
 *    Stockwerken, rund 115 px je Befund, bei acht Befunden über 900 px, bevor
 *    der erste Abschnitt kommt (Foto `390_betrieb-grund`,
 *    `390_betrieb-kopf-mobil`).
 *
 *    Die Form ist `[Pille] [Satz, darf umbrechen] [Pfeil-Knopf]`. Der Knopf ist
 *    ein `IconButton` mit `aria-label` gleich dem Textknopf-Namen
 *    (`BAND_ZIEL_LABEL`), damit `getByRole('button', { name: 'Daily close' })`
 *    auf jeder Breite derselbe Treffer bleibt; die 44-px-Trefferfläche kommt
 *    aus dem Theme (`MuiIconButton` unter `sm`, `theme.js`) und wird hier NICHT
 *    nachgebaut. Eine Zeile ohne Ziel (`unknown`) trägt keinen Knopf.
 *
 * 🔴 **Oben bündig, nicht mittig** (S8-NB1, 2026-11-02): bricht der Satz auf
 *    mehrere Zeilen um, stand die Pille mit `alignItems: 'center'` mittig
 *    daneben statt an der ersten Satzzeile. Der schmale Zweig steht deshalb auf
 *    `flex-start`. Der Pfeil-Knopf trägt `alignSelf: 'center'` selbst — er ist
 *    das Tippziel der GANZEN Zeile und gehört in ihre Mitte, nicht an ihren
 *    Anfang. Einzeilige Befunde sehen danach aus wie vorher oder weichen nur um
 *    die Höhendifferenz Pille zu Satz ab; der breite Zweig ist unverändert.
 *
 * 🔴 **Die Ladezeile traegt KEINE Pille** (R16-W1). Sie ist keine Aussage über
 *    den Zustand der Anlage, sondern über den Abruf; „Unknown" wäre für einen
 *    Moment behauptet, wo nur geladen wird. Der Satz „Checking … k of 6
 *    answered" traegt die Aussage allein. Die Ausfallzeilen behalten „Unknown".
 *
 * 🔴 **`aria-describedby` an BEIDEN Knopfformen** (R16-W3) — auf den Satz der
 *    EIGENEN Zeile. Ohne das hoert ein Screenreader viermal „Daily close" ohne
 *    Bezug; der sichtbare Name bleibt unveraendert, `getByRole('button', { name:
 *    'Daily close' })` trifft weiter. Die Id kommt aus EINEM `useId` plus Index,
 *    nicht aus einem Hook in der Schleife.
 *
 * 🔴 **Auf 390 ist die ganze ZEILE das Tippziel** (R16-W4): `onClick` und
 *    `cursor: pointer` am `Stack`, nur wenn `schmal && zeile.ziel`. Der
 *    `IconButton` bleibt ein echter Knopf (Tastatur, Name, Trefferfläche) und
 *    stoppt die Weitergabe — ein Tipp auf den Pfeil springt EINMAL, nicht
 *    zweimal. Keine Rolle am `Stack`: er ist kein zweiter Knopf im Baum.
 *
 * 🔴 **Ein Schalter, nicht zwei Fassungen im DOM.** `schmal` kommt aus
 *    `useMediaQuery(down('sm'))` in `Betrieb` und wird durchgereicht; der breite
 *    Zweig bleibt unverändert (Textknopf rechts).
 */
function Aufmerksamkeitsband({ zeilen, springe, schmal }) {
  const bandId = useId()
  if (!zeilen.length) return null
  return (
    <Paper
      elevation={0}
      component="section"
      aria-label="Needs attention"
      data-testid="betrieb-band"
      sx={PANEL_SX}
    >
      <Stack spacing={SP.tight}>
        <Typography sx={{ fontSize: FS.base, fontWeight: 600 }}>Needs attention</Typography>
        {zeilen.map((zeile, i) => {
          const ton = SCHWERE_PILL[zeile.schwere]
          const satzId = `${bandId}-satz-${i}`
          const tippbar = schmal && Boolean(zeile.ziel)
          return (
            <Stack
              key={`${zeile.schwere}-${zeile.satz}`}
              direction={schmal ? 'row' : { xs: 'column', sm: 'row' }}
              spacing={schmal ? SP.chip : { xs: SP.chip, sm: SP.tight }}
              alignItems={schmal ? 'flex-start' : { xs: 'flex-start', sm: 'center' }}
              data-testid="betrieb-band-zeile"
              onClick={tippbar ? () => springe(zeile.ziel) : undefined}
              sx={tippbar ? { cursor: 'pointer' } : undefined}
            >
              {!zeile.laden && (
                <Pill tone={ton.tone} nowrap data-testid="betrieb-band-pille" sx={{ flexShrink: 0 }}>
                  {ton.label}
                </Pill>
              )}
              <Box data-testid="betrieb-band-satz" id={satzId} sx={{ flex: 1, minWidth: 0 }}>
                <Typography component="span" sx={{ fontSize: FS.base }}>{zeile.satz}</Typography>
                {zeile.hinweis && (
                  <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                    {' '}· {zeile.hinweis}
                  </Typography>
                )}
              </Box>
              {zeile.ziel && (schmal ? (
                <IconButton
                  size="small"
                  aria-label={BAND_ZIEL_LABEL[zeile.ziel]}
                  aria-describedby={satzId}
                  onClick={(e) => { e.stopPropagation(); springe(zeile.ziel) }}
                  sx={{ flexShrink: 0, alignSelf: 'center' }}
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
              ) : (
                <Button size="small" aria-describedby={satzId} onClick={() => springe(zeile.ziel)}>
                  {BAND_ZIEL_LABEL[zeile.ziel]}
                </Button>
              ))}
            </Stack>
          )
        })}
      </Stack>
    </Paper>
  )
}

/**
 * Ein Anker scrollt und setzt den Fokus.
 *
 * 🔴 Die Bewegungssperre steht im HAUSHELFER (`utils/scroll.js`) — er traegt die
 *    `prefers-reduced-motion`-Abfrage UND das `try/catch` für Umgebungen ohne
 *    `matchMedia`; hier nicht ein zweites Mal (R16-W2, `VendorAnschriften`).
 *    jsdom kennt `scrollIntoView` nicht — der Helfer steigt dann still aus, der
 *    Fokus ist der tragende Teil.
 *
 * 🔴 **Der Fokusring gehoert zum SPRUNG, nicht zu jedem Fokus** (R15-W1). Der
 *    Sprung setzt am Ziel `data-sprungziel="aktiv"` — daran haengt der Ring in
 *    `Abschnitt` — und nimmt es beim Verlassen zurueck (`blur`, einmalig JE
 *    LISTENER). Ein gewoehnlicher Klick in den Abschnittskoerper fokussiert das
 *    `section` zwar auch, setzt das Merkmal aber NICHT: kein Ring ohne Sprung.
 *
 *    🔴 **„Einmalig" meint `{ once: true }` je Listener, nicht einen Listener
 *    insgesamt.** Ein Sprung auf ein bereits fokussiertes Ziel feuert kein
 *    `blur`; der vorige Listener bleibt dann stehen, und der naechste echte
 *    Fokusverlust raeumt alle ab. Unschaedlich: jeder entfernt dasselbe
 *    Attribut, `removeAttribute` auf ein fehlendes ist ein No-op. Die Mechanik
 *    (und diese Begruendung) steht in `utils/sprungziel.js`; `InvoiceList.jsx`
 *    braucht sie fuer das Paneel ebenso.
 */
function springeZu(ziel) {
  if (!ziel) return
  scrollIntoViewRespectingMotion(ziel, { block: 'start' })
  markiereSprungziel(ziel)
  ziel.focus({ preventScroll: true })
}

export default function Betrieb({ meldeKopf, zuRefunds }) {
  const theme = useTheme()
  const schmal = useMediaQuery(theme.breakpoints.down('sm'))

  // Vorgaben: der laufende Monat (bis heute) und heute — Berliner Kalendertag.
  // Nur die VORGABE wird beim ersten Aufbau bestimmt; welcher Tag „heute" ist,
  // fragt der Tagesabschluss bei jedem Aufbau neu.
  const [vorgabe] = useState(() => berlinDateISO(new Date()))
  const [von, setVon] = useState(() => `${vorgabe.slice(0, 8)}01`)
  const [bis, setBis] = useState(vorgabe)
  const [tag, setTag] = useState(vorgabe)

  const zeitraumFehler = pruefeZeitraum(von, bis)
  const tagFehler = alsTag(tag) === null ? 'Give a day.' : null

  const kreise = useLesen('/api/invoicing/admin/nummernkreise', {})
  const beobachtung = useLesen('/api/invoicing/admin/steuerbeobachtung', zeitraumFehler ? null : { von, bis })
  const abschluss = useLesen('/api/invoicing/admin/tagesabschluss', tagFehler ? null : { tag })
  const nachweise = useLesen('/api/invoicing/admin/igl-nachweise', {})

  // 🔴 **Zwei zusätzliche, GEFILTERTE Aufrufe für das Band** (§1.6). „Ohne
  //    Parameter“ heißt ausgeschrieben `{}`, nie ohne Argument: `useLesen`
  //    rechnet `JSON.stringify(undefined)` = `undefined`, und `undefined !== null`
  //    ist wahr — der Effekt riefe `JSON.parse(undefined)` und würfe beim Mount.
  //    Ohne Parameter heißt GEFILTERT (`admin_routes.py:288-289`, `lagen._tb`):
  //    die Arbeitslisten-Zahlen sind ungefiltert sämtlich von Testbelegen getrieben.
  const kennzahlenBand = useLesen('/api/invoicing/admin/kennzahlen', {})
  const warnungenBand = useLesen('/api/invoicing/admin/warnungen', {})

  // 🔴 **Die siebte Quelle (S8):** `/ausfuhr-nachweise`. Sie trägt KEINE Band-Zeile
  //    — die Fläche filtert nicht, eine Band-Zeile hinge an Testbelegen (a2,
  //    01.11.). Sie speist nur den Kopf-Hinweis von „Month close" und den
  //    Abschnitt darin. Sie zählt deshalb NICHT in `BAND_QUELLEN`: die Ladezeile
  //    bleibt „k of 6".
  const ausfuhr = useLesen('/api/invoicing/admin/ausfuhr-nachweise', {})

  const { neuLaden: kreiseNeu } = kreise
  const { neuLaden: beobachtungNeu } = beobachtung
  const { neuLaden: abschlussNeu } = abschluss
  const { neuLaden: nachweiseNeu } = nachweise
  const { neuLaden: kennzahlenNeu } = kennzahlenBand
  const { neuLaden: warnungenNeu } = warnungenBand
  const { neuLaden: ausfuhrNeu } = ausfuhr
  // 🔴 Der Refresh lädt ALLE SIEBEN Flächen — die vier geladenen Antworten, die
  //    zwei Band-Aufrufe und (S8) die Ausfuhr. Ohne die zwei setzte der Klick
  //    „Loaded“ neu, während die Band-Zahlen auf dem Stand des Seitenaufbaus
  //    blieben; ohne die siebte stünde der Kopf-Hinweis still (W7).
  const allesNeuLaden = useCallback(() => {
    kreiseNeu()
    beobachtungNeu()
    abschlussNeu()
    nachweiseNeu()
    kennzahlenNeu()
    warnungenNeu()
    ausfuhrNeu()
  }, [kreiseNeu, beobachtungNeu, abschlussNeu, nachweiseNeu, kennzahlenNeu, warnungenNeu, ausfuhrNeu])

  // ── Die sechs Quellen als eine Aussage: geantwortet oder nicht (F5) ──────
  const quellen = {
    kennzahlen: { ...kennzahlenBand, eingabeFehler: null, geantwortet: !kennzahlenBand.laedt && !kennzahlenBand.fehler && kennzahlenBand.daten !== null },
    warnungen: { ...warnungenBand, eingabeFehler: null, geantwortet: !warnungenBand.laedt && !warnungenBand.fehler && warnungenBand.daten !== null },
    abschluss: { ...abschluss, eingabeFehler: tagFehler, geantwortet: !tagFehler && !abschluss.laedt && !abschluss.fehler && abschluss.daten !== null },
    beobachtung: { ...beobachtung, eingabeFehler: zeitraumFehler, geantwortet: !zeitraumFehler && !beobachtung.laedt && !beobachtung.fehler && beobachtung.daten !== null },
    kreise: { ...kreise, eingabeFehler: null, geantwortet: !kreise.laedt && !kreise.fehler && kreise.daten !== null },
    igl: { ...nachweise, eingabeFehler: null, geantwortet: !nachweise.laedt && !nachweise.fehler && nachweise.daten !== null },
  }
  const zeilen = bandZeilen(quellen)
  const antworten = Object.values(quellen).filter((q) => q.geantwortet).length
  const irgendwasLaedt = Object.values(quellen).some((q) => q.laedt)
  // 🔴 Solange eine Quelle läuft, steht GENAU EINE Ladezeile für alle — keine je
  //    Quelle. Ein hängender Abruf sieht damit nicht wie „nichts offen“ aus.
  const band = irgendwasLaedt
    ? [{ schwere: 'unknown', satz: `Checking … ${antworten} of ${BAND_QUELLEN} answered`, ziel: null, laden: true }, ...zeilen]
    : zeilen

  // ── „Loaded hh:mm“ — die Client-Zeit, wenn keine Quelle mehr lädt ────────
  // Der Stempel sagt „so weit sind wir“, nicht „alles gut“: er entsteht auch,
  // wenn eine Quelle gescheitert ist. Beim Neuladen bleibt der alte stehen, bis
  // der neue da ist; vor der ersten vollständigen Antwort gibt es keinen.
  const alleFertig = !irgendwasLaedt
  const [ladezeit, setLadezeit] = useState(null)
  useEffect(() => {
    if (alleFertig) setLadezeit(new Date())
  }, [alleFertig])

  // ── Anker ────────────────────────────────────────────────────────────────
  // 🔴 Je Ziel ein EIGENER Ref, keine Objektliterale in den Abhängigkeiten:
  //    ein je Render neues `anker`-Objekt machte `springe` und `handfallSprung`
  //    instabil, der Melde-Effekt liefe bei jedem Render und schriebe den Kopf
  //    des Elternbaums neu — eine Schleife.
  const ankerDailyClose = useRef(null)
  const ankerNummernkreise = useRef(null)
  const ankerSteuer = useRef(null)
  const ankerIgl = useRef(null)
  const ankerHandfall = useRef(null)

  // 🔴 **S8: Month close ist zu — ein Anker in eines seiner Kinder muss aufklappen.**
  //    Der Zähler ist der `key` des `CollapsibleSection` (`Abschnitt`), der
  //    Wechsel montiert neu und `defaultExpanded` greift. Der Sprung läuft DANACH
  //    (Effekt auf den Zähler), sonst misst `scrollIntoView` die noch zugeklappte
  //    Lage und `.focus()` träfe ein `visibility: hidden`-Ziel.
  const [monatLauf, setMonatLauf] = useState(0)
  const warteAufZiel = useRef(null)
  const springe = useCallback((ziel) => {
    if (ziel === 'refunds') {
      // Ein Reiter-Ziel läuft über den VORHANDENEN Reiterzustand (`?tab=`) —
      // kein zweiter Mechanismus. Den Fokus setzt die Schale auf `liste-panel`.
      zuRefunds?.()
      return
    }
    const ziele = {
      'daily-close': ankerDailyClose,
      nummernkreise: ankerNummernkreise,
      steuerbeobachtung: ankerSteuer,
      igl: ankerIgl,
    }
    if (MONAT_KINDER.includes(ziel)) {
      warteAufZiel.current = ziele[ziel]
      setMonatLauf((n) => n + 1)
      return
    }
    springeZu(ziele[ziel]?.current)
  }, [zuRefunds])

  // Läuft NACH dem Render, in dem der `key` gewechselt hat — dann ist der Inhalt
  // sichtbar (`Collapse` nimmt `visibility: hidden` zurück) und der Sprung trifft.
  useEffect(() => {
    if (!warteAufZiel.current) return
    const ziel = warteAufZiel.current
    warteAufZiel.current = null
    springeZu(ziel.current)
  }, [monatLauf])

  const handfallSprung = useCallback(() => springeZu(ankerHandfall.current), [])

  // ── Der Melde-Weg an den Seitenkopf (Kopf-Naht, §1.6) ────────────────────
  // 🔴 `Betrieb` hält die Hooks, `InvoiceList` rendert den Kopf. Die Schale
  //    bekommt NUR Bedienung, keine Daten — und beim Unmount `null`, sonst
  //    bliebe ein toter Refresh im Kopf der anderen Reiter stehen.
  // 🔴 `useLayoutEffect`, nicht `useEffect`: der Aufräumer läuft VOR dem Paint.
  //    Ein passiver Aufräumer liefe nach dem Paint, und die Schale rendert beim
  //    Reiterwechsel Operations → Invoices genau einen Frame lang mit dem ALTEN
  //    `kopfMeldung` („Loaded", „Manual invoice", Operations-Refresh über der
  //    Rechnungsliste). R15-W4; jsdom sieht den Frame nicht.
  useLayoutEffect(() => {
    if (!meldeKopf) return undefined
    meldeKopf({ neuLaden: allesNeuLaden, ladezeit, handfallSprung })
    return () => meldeKopf(null)
  }, [meldeKopf, allesNeuLaden, ladezeit, handfallSprung])

  return (
    <Stack spacing={SP.section}>
      <Aufmerksamkeitsband zeilen={band} springe={springe} schmal={schmal} />
      {/* 🔴 S8 — die Reihenfolge: Band → Daily close → Manual invoice → Month close.
          Was hängt, steht vorn; was heute passiert ist, danach; der Handfall (er
          liegt unter dem Falz und hat seinen Sprung im Kopf); der Monat zuletzt,
          zugeklappt. */}
      <Abschnitt titel="Daily close" tooltip="Tagesabschluss" ankerRef={ankerDailyClose}>
        <Tagesabschluss
          stand={abschluss}
          schmal={schmal}
          tag={tag}
          setTag={setTag}
          fehler={tagFehler}
        />
      </Abschnitt>
      <Abschnitt titel="Manual invoice" ankerRef={ankerHandfall}>
        <ManuelleRechnungWahl />
      </Abschnitt>
      {/* 🔴 **Month close klappt auf ALLEN Breiten zu** (§1.6). Die Zuklapp-Bedingung
          hängt am Kopf-Hinweis: jeder Befund dieses Abschnitts steht im Band, die
          Ausfuhr steht sichtbar hier im Titel (sie hat keine Band-Zeile, weil
          `/ausfuhr-nachweise` nicht filtert). */}
      <Abschnitt
        titel="Month close"
        tooltip="Monatsabschluss"
        defaultExpanded={false}
        ankerLauf={monatLauf}
        hinweis={ausfuhrKopfHinweis(ausfuhr)}
      >
        {/* 🔴 R16 C1: die vier Kinder stehen eine Stufe UNTER ihrem Elternteil.
            Ohne `titleVariant` wären sie alle `h6` wie „Month close" selbst —
            fünf gleich laute Überschriften statt eines Elternteils mit vier
            Kindern (Foto `1440_betrieb-month-close-offen`). `subtitle1` ist der
            vorgesehene Slot des geteilten Bausteins; kein neuer Prop dort. */}
        <Stack spacing={SP.section}>
          <Abschnitt titel="Number ranges" titleVariant="subtitle1" tooltip="Nummernkreis" ankerRef={ankerNummernkreise}>
            <Nummernkreise stand={kreise} schmal={schmal} />
          </Abschnitt>
          <Abschnitt titel="Tax monitoring" titleVariant="subtitle1" tooltip="Steuerbeobachtung" ankerRef={ankerSteuer}>
            <Steuerbeobachtung
              stand={beobachtung}
              schmal={schmal}
              von={von}
              bis={bis}
              setVon={setVon}
              setBis={setBis}
              fehler={zeitraumFehler}
            />
          </Abschnitt>
          <Abschnitt titel="Proofs of arrival (intra-Community)" titleVariant="subtitle1" tooltip="Gelangensnachweise (IgL)" ankerRef={ankerIgl}>
            <IglNachweise stand={nachweise} schmal={schmal} />
          </Abschnitt>
          <Abschnitt titel="Export proofs" titleVariant="subtitle1" tooltip="Ausfuhrnachweise">
            <Ausfuhrnachweise stand={ausfuhr} schmal={schmal} />
          </Abschnitt>
        </Stack>
      </Abschnitt>
    </Stack>
  )
}
